import {
  CATEGORIES,
  DEFAULT_TRANSITION_POLICY,
  DEFINITION_KINDS,
  LEVELS,
  ROLES,
  TRANSITION_POLICY_DESCRIPTION,
  __export,
  define,
  definition,
  definition2,
  definition3,
  definition4,
  definition5,
  definition6,
  transitionPolicySchema
} from "./chunk-B45MFSSL.mjs";

// src/definitions/version.ts
var version_exports = {};
__export(version_exports, {
  definition: () => definition7,
  schema: () => schema
});
import { z } from "zod";
var schema = z.string();
var definition7 = define({
  key: "version",
  schema,
  label: "Version",
  description: 'Configuration schema version for compatibility (e.g., "1.0.0").',
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.METADATA.key,
  writableAtLevels: [
    LEVELS.HOPDRIVE,
    LEVELS.REGION,
    LEVELS.ORGANIZATION,
    LEVELS.CUSTOMER,
    LEVELS.WORKFLOWSET,
    LEVELS.CONTRACT,
    LEVELS.MOVE
  ],
  readableByRoles: [ROLES.ADMIN]
});

// src/definitions/api-enabled.ts
var api_enabled_exports = {};
__export(api_enabled_exports, {
  definition: () => definition8,
  schema: () => schema2
});
import { z as z2 } from "zod";
var schema2 = z2.boolean();
var definition8 = define({
  key: "api_enabled",
  schema: schema2,
  label: "API Enabled",
  description: "Activate/Deactivate the API feature for clients at this level. Currently, this is only a visual indicator of the client to show that they use the API. In the future, this switch may behave differently.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.INTEGRATIONS.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: false
});

// src/definitions/api-config.ts
var api_config_exports = {};
__export(api_config_exports, {
  definition: () => definition9,
  schema: () => schema3
});
import { z as z4 } from "zod";

// src/types/encryptedString.ts
import { z as z3 } from "zod";
var ENCRYPTED_STRING_PATTERN = /^encrypted:(v\d+):(.+)$/;
var encryptedString = z3.string().refine((val) => ENCRYPTED_STRING_PATTERN.test(val), {
  message: "Must be a valid encrypted string format: encrypted:{version}:{ciphertext}"
}).brand();
function isEncryptedString(value) {
  return typeof value === "string" && ENCRYPTED_STRING_PATTERN.test(value);
}
function getEncryptionVersion(value) {
  const match = value.match(ENCRYPTED_STRING_PATTERN);
  return match ? match[1] : null;
}
function getCiphertext(value) {
  const match = value.match(ENCRYPTED_STRING_PATTERN);
  return match ? match[2] : null;
}

// src/definitions/api-config.ts
var schema3 = z4.object({
  /**
   * Primary HMAC shared secret (encrypted).
   * Used to generate hash values for anonymous JWT handshake authentication.
   */
  hmac_shared_secret_1: encryptedString.describe("Primary HMAC shared secret for anonymous JWT handshake authentication").optional(),
  /**
   * Secondary HMAC shared secret (encrypted).
   * Used for key rotation - allows switching to a new secret without service interruption.
   */
  hmac_shared_secret_2: encryptedString.describe("Secondary HMAC shared secret for key rotation support").optional(),
  /**
   * OAuth Client ID for HopDrive REST API access.
   */
  oauth_client_id: z4.string().describe("OAuth Client ID for HopDrive REST API access").optional(),
  /**
   * OAuth Client Secret for HopDrive REST API access (encrypted).
   */
  oauth_client_secret: encryptedString.describe("OAuth Client Secret for HopDrive REST API access").optional()
}).strict().describe("API configuration for customer/organization authentication and API access");
var definition9 = define({
  key: "api_config",
  schema: schema3,
  label: "API Configuration",
  description: "Customer/organization API configuration including HMAC shared secrets for anonymous JWT authentication and OAuth credentials for HopDrive REST API access.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.INTEGRATIONS.key,
  warning: "Contains sensitive authentication secrets. HMAC secrets and OAuth credentials must be encrypted before storage.",
  writableAtLevels: [LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN]
});

// src/definitions/price-estimates.ts
var price_estimates_exports = {};
__export(price_estimates_exports, {
  definition: () => definition10,
  schema: () => schema4
});
import { z as z5 } from "zod";
var schema4 = z5.boolean();
var definition10 = define({
  key: "price_estimates",
  schema: schema4,
  label: "Activate Price Estimates",
  description: "Activate/Deactivate the price estimates feature for this client. This will allow the client to see price estimates for their moves before they create them.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.MOVE_PLANNING.key,
  writableAtLevels: [
    LEVELS.HOPDRIVE,
    LEVELS.REGION,
    LEVELS.ORGANIZATION,
    LEVELS.CUSTOMER,
    LEVELS.WORKFLOWSET,
    LEVELS.CONTRACT
  ],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: false
});

// src/definitions/distance-estimates.ts
var distance_estimates_exports = {};
__export(distance_estimates_exports, {
  definition: () => definition11,
  schema: () => schema5
});
import { z as z6 } from "zod";
var schema5 = z6.boolean();
var definition11 = define({
  key: "distance_estimates",
  schema: schema5,
  label: "Activate Distance Estimates",
  description: "Activate/Deactivate the distance estimates feature for clients at this level. This will allow the client to see distance estimates for their moves before they create them.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.MOVE_PLANNING.key,
  writableAtLevels: [
    LEVELS.HOPDRIVE,
    LEVELS.REGION,
    LEVELS.ORGANIZATION,
    LEVELS.CUSTOMER,
    LEVELS.WORKFLOWSET,
    LEVELS.CONTRACT
  ],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: false
});

// src/definitions/allow-email-notifications.ts
var allow_email_notifications_exports = {};
__export(allow_email_notifications_exports, {
  definition: () => definition12,
  schema: () => schema6
});
import { z as z7 } from "zod";
var schema6 = z7.boolean();
var definition12 = define({
  key: "allow_email_notifications",
  schema: schema6,
  label: "Move Status Updates",
  description: "Select whether clients at this level prefer their users to receive email notifications when a move has been picked up or delivered successfully. If selected, the user who created the move, as well as an optional list of other users specified during move planning, will receive a notification via email.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.NOTIFICATIONS.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: false
});

// src/definitions/notify-contact-of-outcomes.ts
var notify_contact_of_outcomes_exports = {};
__export(notify_contact_of_outcomes_exports, {
  definition: () => definition13,
  schema: () => schema7
});
import { z as z8 } from "zod";
var schema7 = z8.boolean();
var definition13 = define({
  key: "notify_contact_of_outcomes",
  schema: schema7,
  label: "Notify Contacts on Outcome Resolution",
  description: "Select whether the move's pickup and delivery contacts should be cc'd on the email sent when an outcome on the move is resolved. The user who created the move is always notified.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.OUTCOMES.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: false
});

// src/definitions/dealer-visibility-enabled.ts
var dealer_visibility_enabled_exports = {};
__export(dealer_visibility_enabled_exports, {
  definition: () => definition14,
  schema: () => schema8
});
import { z as z9 } from "zod";
var schema8 = z9.boolean();
var definition14 = define({
  key: "dealer_visibility_enabled",
  schema: schema8,
  label: "Dealer Outcome Visibility",
  description: "Gates whether dealers see resolved move outcomes on the dealer-portal/embed read-only surface. This is a feature flag for a Part 2 (not-yet-shipped) dealer-facing outcomes surface.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.OUTCOMES.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: false
});

// src/definitions/customer-feedback-recipients-fallback.ts
var customer_feedback_recipients_fallback_exports = {};
__export(customer_feedback_recipients_fallback_exports, {
  definition: () => definition15,
  schema: () => schema9
});
import { z as z10 } from "zod";
var schema9 = z10.array(z10.string().email());
var definition15 = define({
  key: "customer_feedback_recipients_fallback",
  schema: schema9,
  label: "Customer Feedback Recipients (Fallback)",
  description: "Explicit fallback email list used by the outcome-feedback recipient resolver when the move's contacts and the customer's contact list are both empty. Referenced by Part 1 Decision 21 (move_contacts_with_customer_fallback resolver).",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.OUTCOMES.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: []
});

// src/definitions/allow-triangle-moves.ts
var allow_triangle_moves_exports = {};
__export(allow_triangle_moves_exports, {
  definition: () => definition16,
  schema: () => schema10
});
import { z as z11 } from "zod";
var schema10 = z11.boolean();
var definition16 = define({
  key: "allow_triangle_moves",
  schema: schema10,
  label: "Activate Triangle Moves",
  description: "Activate/Deactivate the triangle moves feature for clients at this level. Triangle moves allow for price adjustments to a ride-drive-ride bundle of moves.",
  warning: "If you do not understand what this feature does, please leave it disabled.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.MOVE_PLANNING.key,
  writableAtLevels: [
    LEVELS.HOPDRIVE,
    LEVELS.REGION,
    LEVELS.ORGANIZATION,
    LEVELS.CUSTOMER,
    LEVELS.WORKFLOWSET,
    LEVELS.CONTRACT
  ],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: false
});

// src/definitions/enable-csv-import.ts
var enable_csv_import_exports = {};
__export(enable_csv_import_exports, {
  definition: () => definition17,
  schema: () => schema11
});
import { z as z12 } from "zod";
var schema11 = z12.boolean();
var definition17 = define({
  key: "enable_csv_import",
  schema: schema11,
  label: "Enable CSV Import",
  description: "Activate/Deactivate the CSV import feature for customers in Portal. When enabled, the customer can import moves via CSV file upload.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.MOVE_PLANNING.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: false
});

// src/definitions/public-workflowsets.ts
var public_workflowsets_exports = {};
__export(public_workflowsets_exports, {
  definition: () => definition18,
  schema: () => schema12
});
import { z as z13 } from "zod";
var schema12 = z13.boolean();
var definition18 = define({
  key: "public_workflowsets",
  schema: schema12,
  label: "Show Public Workflowsets",
  description: "LEGACY: Controls whether public workflowsets are publicly visible or not.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.WORKFLOWS.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.REGION, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: true,
  deprecated: true
});

// src/definitions/rideshare-calling-enabled.ts
var rideshare_calling_enabled_exports = {};
__export(rideshare_calling_enabled_exports, {
  definition: () => definition19,
  schema: () => schema13
});
import { z as z14 } from "zod";
var schema13 = z14.boolean();
var definition19 = define({
  key: "rideshare_calling_enabled",
  schema: schema13,
  label: "Rideshare Calling Enabled",
  description: "Hopdrive Lite only. This setting will determine whether Lite clients at this level have opted to enable calling rideshares for their drivers.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.RIDESHARE.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.REGION, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: false
});

// src/definitions/ride-provider-algorithm-key.ts
var ride_provider_algorithm_key_exports = {};
__export(ride_provider_algorithm_key_exports, {
  definition: () => definition20,
  schema: () => schema14
});
import { z as z15 } from "zod";
var schema14 = z15.string();
var definition20 = define({
  key: "ride_provider_algorithm_key",
  schema: schema14,
  label: "Ride Provider Selection Algorithm",
  description: "Select the algorithm when choosing between ride providers. Each algorithm has different selection criteria and logic.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.RIDESHARE.key,
  writableAtLevels: [
    LEVELS.REGION,
    LEVELS.ORGANIZATION,
    LEVELS.CUSTOMER,
    LEVELS.WORKFLOWSET,
    LEVELS.CONTRACT,
    LEVELS.MOVE
  ],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: "ride-provider-cheapest-v1",
  storage: {
    enum: {
      from: "algorithms",
      select: {
        key: true,
        label: true,
        description: true,
        params: true
      },
      where: {
        type: { _eq: "ride-provider-selection" }
      },
      orderBy: { key: "asc" },
      mapping: {
        value: "key",
        label: "label",
        description: "description",
        metadata: {
          params: "params"
        }
      }
    }
    // Default JSONB storage in config column - no levels config needed
  }
});

// src/definitions/passthrough-rideshare-surges.ts
var passthrough_rideshare_surges_exports = {};
__export(passthrough_rideshare_surges_exports, {
  definition: () => definition21,
  schema: () => schema15
});
import { z as z16 } from "zod";
var schema15 = z16.boolean();
var definition21 = define({
  key: "passthrough_rideshare_surges",
  schema: schema15,
  label: "Surge Charge Accountability",
  description: "Activate/Deactivate rideshare surge charges for this client. Surge charges are incurred when the actual rideshare cost is higher than anticipated. When active, the client will cover any rideshare surge charges related to their moves. When inactive, HopDrive will cover the extra cost and waive all rideshare surge charges for this client.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.PRICING.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.ORGANIZATION, LEVELS.CUSTOMER, LEVELS.CONTRACT],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: true
});

// src/definitions/require-third-party-payer.ts
var require_third_party_payer_exports = {};
__export(require_third_party_payer_exports, {
  definition: () => definition22,
  schema: () => schema16
});
import { z as z17 } from "zod";
var schema16 = z17.boolean();
var definition22 = define({
  key: "require_third_party_payer",
  schema: schema16,
  label: "Require Third Party Payer",
  description: "Require the client to input a third party payer when creating moves through the portal or API.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.THIRD_PARTY_PAYER.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: false
});

// src/definitions/auto-assign-ref.ts
var auto_assign_ref_exports = {};
__export(auto_assign_ref_exports, {
  definition: () => definition23,
  schema: () => schema17
});
import { z as z18 } from "zod";
var schema17 = z18.boolean();
var definition23 = define({
  key: "auto_assign_ref",
  schema: schema17,
  label: "Auto-Assign Ref",
  description: "When clients at this level are creating a concierge return move, automatically assign its reference number to the related concierge pickup of this move.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.REFERENCE_NUMBER.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: false
});

// src/definitions/default-move-type.ts
var default_move_type_exports = {};
__export(default_move_type_exports, {
  MoveTypeSchema: () => MoveTypeSchema,
  MoveTypes: () => MoveTypes,
  definition: () => definition24,
  schema: () => schema18
});
import { z as z19 } from "zod";
var MoveTypes = ["concierge", "concierge-loaner", "one-way", "round-trip"];
var MoveTypeSchema = z19.enum(MoveTypes);
var schema18 = MoveTypeSchema;
var definition24 = define({
  key: "default_move_type",
  schema: schema18,
  label: "Default Move Type",
  description: "Default move type pre-selected for new moves. Users can change, but this sets initial selection.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.MOVE_PLANNING.key,
  writableAtLevels: [
    LEVELS.HOPDRIVE,
    LEVELS.REGION,
    LEVELS.ORGANIZATION,
    LEVELS.CUSTOMER,
    LEVELS.WORKFLOWSET,
    LEVELS.CONTRACT
  ],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: "concierge"
});

// src/definitions/allowed-move-types.ts
var allowed_move_types_exports = {};
__export(allowed_move_types_exports, {
  definition: () => definition25,
  schema: () => schema19
});
import { z as z20 } from "zod";
var schema19 = z20.array(MoveTypeSchema);
var definition25 = define({
  key: "allowed_move_types",
  schema: schema19,
  label: "Allowed Move Types",
  description: "Choose which move types are allowed for clients at this level in the move planner. More often than not, there is no reason to restrict a client unless they request/require it.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.MOVE_PLANNING.key,
  writableAtLevels: [
    LEVELS.HOPDRIVE,
    LEVELS.REGION,
    LEVELS.ORGANIZATION,
    LEVELS.CUSTOMER,
    LEVELS.WORKFLOWSET,
    LEVELS.CONTRACT
  ],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: MoveTypes
});

// src/definitions/allowed-templates.ts
var allowed_templates_exports = {};
__export(allowed_templates_exports, {
  definition: () => definition26,
  schema: () => schema20
});
import { z as z21 } from "zod";
var schema20 = z21.array(z21.string());
var definition26 = define({
  key: "allowed_templates",
  schema: schema20,
  label: "Allowed Templates",
  description: "LEGACY - Allowed template identifiers. Restricts which move templates this customer can use.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.MOVE_PLANNING.key,
  writableAtLevels: [LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
  deprecated: true
});

// src/definitions/add-moves-always.ts
var add_moves_always_exports = {};
__export(add_moves_always_exports, {
  definition: () => definition27,
  schema: () => schema21
});
import { z as z22 } from "zod";
var schema21 = z22.boolean();
var definition27 = define({
  key: "add_moves_always",
  schema: schema21,
  label: "Bypass Auto-Deactivation",
  description: "Bypass the automatic deactivation of move planning. As mentioned in the Move Planning Activation option, move planning will deactivate automatically when a client goes over their move limit. This switch will override that behavior and allow the client to continue creating moves.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.MOVE_PLANNING.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN, ROLES.DEALER_ADMIN, ROLES.DRIVER],
  defaultValue: true
});

// src/definitions/sixt-settings.ts
var sixt_settings_exports = {};
__export(sixt_settings_exports, {
  definition: () => definition28,
  schema: () => schema22
});
import { z as z23 } from "zod";
var schema22 = z23.object({
  slackChannel: z23.string().optional().describe(
    "Slack channel HopBot posts Sixt order intake outcomes to (handled / needs-decision / blocked), without the leading `#`. **HopBot must be invited to the channel or nothing posts.** Leave blank to use the integration's default channel (SLACK_CHANNEL). Organization level only \u2014 a customer-level value is not read."
  ),
  customerMatchMaxMiles: z23.number().positive().optional().describe(
    "Farthest a Sixt customer record can be from an order's pickup station (straight-line miles) and still be auto-assigned the batch's moves. The nearest candidate beyond this parks the job Blocked for dispatch. Leave blank to use the integration default (100). Organization level only \u2014 the match runs before any customer is known."
  ),
  customerMatchTieMiles: z23.number().min(0).optional().describe(
    "When the two nearest Sixt customer records are within this many straight-line miles of each other the match is ambiguous and the job parks Blocked for dispatch. Leave blank to use the integration default (5). Organization level only \u2014 the match runs before any customer is known."
  ),
  locationMatchMaxMiles: z23.number().positive().optional().describe(
    "Farthest a seeded location may sit from an order's station (straight-line miles) and still be treated as that station. Stations are geocoded from their address, so a large site \u2014 an airport rental centre, an auction lot, a de-fleet yard \u2014 can place well over a mile from the stored coordinate and block the order as an unknown location. Leave blank to use the integration default (3). A customer-level value overrides the organization's for that customer's orders, since this match runs after the customer is known."
  ),
  moveCreationMode: z23.enum(["off", "approval", "auto"]).optional().describe(
    'How a validated Sixt order becomes HopDrive moves. `approval` (the default): the integration posts an Approve / Reject card to the Slack channel and creates the moves only when a dispatcher approves. `auto`: moves are created immediately, no human step. `off`: intake stops at a ready job and dispatch creates the moves by hand. When set, this replaces the legacy "Create moves automatically" switch. Customer-level values are honored like the switch was.'
  ),
  moveCreationEnabled: z23.boolean().optional().describe(
    "LEGACY \u2014 superseded by moveCreationMode (kept one release so existing values keep their meaning: on = auto, off = off). When on, a validated Sixt order immediately creates HopDrive moves (one per vehicle) under the nearest matched customer. When off (the default), intake stops at a ready job and dispatch creates the moves by hand. The organization switch is the master gate; once an order's customer is matched, a customer-level off holds that customer's orders at ready for manual dispatch."
  ),
  moveRetryMaxAttempts: z23.number().int().positive().optional().describe(
    "How many times the watchdog retries creating a move for one vehicle before giving up and parking it for a human. Leave blank to use the integration default (5). A customer-level value overrides the organization's for that customer's orders."
  ),
  moveRetryBaseSeconds: z23.number().positive().optional().describe(
    "Base delay (seconds) for the watchdog's exponential backoff between move-creation retries \u2014 the wait after attempt N is base \xD7 2^(N-1). Leave blank to use the integration default (60). A customer-level value overrides the organization's for that customer's orders."
  ),
  portalWritesEnabled: z23.boolean().optional().describe(
    "Whether Approve and Reject on the Slack card also accept or decline the contract in the Sixt portal. Off unless set to true. This is one of two gates: the Sixt site must also have SIXT_PORTAL_WRITES=on, and with either one off the integration only records what it would have sent. Organization level only \u2014 a customer-level value is not read."
  )
}).describe("Settings for the Sixt integration.");
var definition28 = define({
  key: "sixt_settings",
  schema: schema22,
  label: "Sixt Integration",
  description: "Settings for the Sixt integration \u2014 the Slack channel its Dispatch Review cards post to, the match bounds move creation uses for customers and for stations, and the move-creation switch. Set at the organization level; a customer-level value applies to the move-creation switch, the retry settings and the station-match bound only, once an order's customer is matched.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.SIXT.key,
  writableAtLevels: [LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: {}
});

// src/definitions/add-moves-disabled.ts
var add_moves_disabled_exports = {};
__export(add_moves_disabled_exports, {
  definition: () => definition29,
  schema: () => schema23
});
import { z as z24 } from "zod";
var schema23 = z24.boolean();
var definition29 = define({
  key: "add_moves_disabled",
  schema: schema23,
  label: "Move Planner Activation",
  description: "Activate/Deactivate the move planning feature for this client. This will allow the client to create moves in our portal or API. NOTE: This switch will deactivate automatically when a client goes over their move limit. Please use the Bypass Auto-Deactivation option to override this behavior.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.MOVE_PLANNING.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: false
});

// src/definitions/cancellation-fee.ts
var cancellation_fee_exports = {};
__export(cancellation_fee_exports, {
  definition: () => definition30,
  schema: () => schema24
});
import { z as z25 } from "zod";
var schema24 = z25.number().nonnegative().max(1e3);
var definition30 = define({
  key: "cancellation_fee",
  schema: schema24,
  label: "Cancellation Fee",
  description: "Amount to charge clients at this level for cancelling a move that has already been started.",
  warning: "This does not apply to dry-run cancellations.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.PRICING.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.REGION, LEVELS.ORGANIZATION, LEVELS.CUSTOMER, LEVELS.CONTRACT],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: 50
});

// src/definitions/delay-price-per-minute.ts
var delay_price_per_minute_exports = {};
__export(delay_price_per_minute_exports, {
  definition: () => definition31,
  schema: () => schema25
});
import { z as z26 } from "zod";
var schema25 = z26.number().nonnegative().max(1);
var definition31 = define({
  key: "delay_price_per_minute",
  schema: schema25,
  label: "Delay Price Per Minute",
  description: "Amount to charge clients at this level per each minute our driver is delayed. This only applies when we choose to specifically charge clients at this level for the delay. Min: $0.00, Max: $1.00.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.PRICING.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.REGION, LEVELS.ORGANIZATION, LEVELS.CUSTOMER, LEVELS.CONTRACT],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: 0.33
});

// src/definitions/fuel-multiplier.ts
var fuel_multiplier_exports = {};
__export(fuel_multiplier_exports, {
  definition: () => definition32,
  schema: () => schema26
});
import { z as z27 } from "zod";
var schema26 = z27.number().nonnegative().max(3);
var definition32 = define({
  key: "fuel_multiplier",
  schema: schema26,
  label: "Fuel Multiplier",
  description: "The multiplier used to calculate the fuel surcharge. This is a multiplier of the base fare. Min: 0.0, Max: 3.0.",
  warning: "Does NOT affect clients with flat rate pricing.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.PRICING.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.ORGANIZATION, LEVELS.CUSTOMER, LEVELS.CONTRACT],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: 1.5
});

// src/definitions/default-workflow-set-id.ts
var default_workflow_set_id_exports = {};
__export(default_workflow_set_id_exports, {
  definition: () => definition33,
  schema: () => schema27
});
import { z as z28 } from "zod";
var schema27 = z28.number().int().positive();
var definition33 = define({
  key: "default_workflow_set_id",
  schema: schema27,
  label: "Default Workflow Set",
  description: "The default workflow set is the preferred set of pickup and delivery workflows used for clients at this level.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.WORKFLOWS.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: 1
});

// src/definitions/pickup-workflow-id.ts
var pickup_workflow_id_exports = {};
__export(pickup_workflow_id_exports, {
  definition: () => definition34,
  schema: () => schema28
});
import { z as z29 } from "zod";
var schema28 = z29.number().int().positive();
var definition34 = define({
  key: "pickup_workflow_id",
  schema: schema28,
  label: "Pickup Workflow ID",
  description: "LEGACY: Workflow ID for pickup operations. Defines driver steps during vehicle pickup.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.WORKFLOWS.key,
  writableAtLevels: [LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
  deprecated: true
});

// src/definitions/delivery-workflow-id.ts
var delivery_workflow_id_exports = {};
__export(delivery_workflow_id_exports, {
  definition: () => definition35,
  schema: () => schema29
});
import { z as z30 } from "zod";
var schema29 = z30.number().int().positive();
var definition35 = define({
  key: "delivery_workflow_id",
  schema: schema29,
  label: "Delivery Workflow ID",
  description: "LEGACY: Workflow ID for delivery operations. Defines driver steps during vehicle delivery.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.WORKFLOWS.key,
  writableAtLevels: [LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
  deprecated: true
});

// src/definitions/default-pickup-workflow-id.ts
var default_pickup_workflow_id_exports = {};
__export(default_pickup_workflow_id_exports, {
  definition: () => definition36,
  schema: () => schema30
});
import { z as z31 } from "zod";
var schema30 = z31.number().int().positive();
var definition36 = define({
  key: "default_pickup_workflow_id",
  schema: schema30,
  label: "Default Pickup Workflow ID",
  description: "Default pickup workflow ID. Auto-populated from workflowset selection.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.WORKFLOWS.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: 1
});

// src/definitions/default-delivery-workflow-id.ts
var default_delivery_workflow_id_exports = {};
__export(default_delivery_workflow_id_exports, {
  definition: () => definition37,
  schema: () => schema31
});
import { z as z32 } from "zod";
var schema31 = z32.number().int().positive();
var definition37 = define({
  key: "default_delivery_workflow_id",
  schema: schema31,
  label: "Default Delivery Workflow ID",
  description: "Default delivery workflow ID. Auto-populated from workflowset selection.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.WORKFLOWS.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: 2
});

// src/definitions/move-workflow-overrides.ts
var move_workflow_overrides_exports = {};
__export(move_workflow_overrides_exports, {
  definition: () => definition38,
  schema: () => schema32
});
import { z as z33 } from "zod";
var WorkflowPairSchema = z33.object({
  pickup_workflow_id: z33.number().int().positive().describe("Pickup workflow ID for this move role.").optional(),
  delivery_workflow_id: z33.number().int().positive().describe("Delivery workflow ID for this move role.").optional()
}).strict();
var schema32 = z33.object({
  operational: WorkflowPairSchema.describe("Workflows used for operational moves.").optional(),
  valet_customer: WorkflowPairSchema.describe("Workflows used for the customer vehicle in a valet move.").optional(),
  valet_loaner: WorkflowPairSchema.describe("Workflows used for the loaner vehicle in a valet move.").optional()
}).strict().describe("Pickup and delivery workflow overrides selected from a move role.");
var definition38 = define({
  key: "move_workflow_overrides",
  schema: schema32,
  label: "Move Workflow Overrides (Preview)",
  description: "Override the workflowset pickup and delivery workflows for operational, valet customer, or valet loaner moves.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.WORKFLOWS.key,
  writableAtLevels: [LEVELS.WORKFLOWSET],
  readableByRoles: [ROLES.ADMIN, ROLES.DRIVER, ROLES.DEALER_ADMIN, ROLES.DEALER_SUPER_ADMIN, ROLES.DISPATCHER]
});

// src/definitions/default-payer.ts
var default_payer_exports = {};
__export(default_payer_exports, {
  definition: () => definition39,
  schema: () => schema33
});
import { z as z34 } from "zod";
var schema33 = z34.number().int().positive().nullable();
var definition39 = define({
  key: "default_payer",
  schema: schema33,
  label: "Default Payer",
  description: "The default payer for moves created through the portal or API. For now, we have to select this from the full list of customers. Please be sure you have selected the correct customer before you save. To remove the default payer, select 'None'.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.THIRD_PARTY_PAYER.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.ORGANIZATION, LEVELS.CUSTOMER, LEVELS.WORKFLOWSET, LEVELS.CONTRACT],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: null
});

// src/definitions/reference-num-label.ts
var reference_num_label_exports = {};
__export(reference_num_label_exports, {
  definition: () => definition40,
  schema: () => schema34
});
import { z as z35 } from "zod";
var schema34 = z35.string();
var definition40 = define({
  key: "reference_num_label",
  schema: schema34,
  label: "Reference ID Label",
  description: 'A client-specific label to display for the reference number field (e.g. "RO Number", "Claim Number", "Fleet ID", etc.).',
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.REFERENCE_NUMBER.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: "Reference ID"
});

// src/definitions/reference-num-required-for-pickup.ts
var reference_num_required_for_pickup_exports = {};
__export(reference_num_required_for_pickup_exports, {
  definition: () => definition41,
  schema: () => schema35
});
import { z as z36 } from "zod";
var schema35 = z36.boolean();
var definition41 = define({
  key: "reference_num_required_for_pickup",
  schema: schema35,
  label: "Ref Required for Concierge Pickup",
  description: "Require the reference number to be entered for concierge pickup moves.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.REFERENCE_NUMBER.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: false
});

// src/definitions/reference-num-required-for-return.ts
var reference_num_required_for_return_exports = {};
__export(reference_num_required_for_return_exports, {
  definition: () => definition42,
  schema: () => schema36
});
import { z as z37 } from "zod";
var schema36 = z37.boolean();
var definition42 = define({
  key: "reference_num_required_for_return",
  schema: schema36,
  label: "Ref Required for Concierge Return",
  description: "Require the reference number to be entered for concierge return moves.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.REFERENCE_NUMBER.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: false
});

// src/definitions/reference-num-report-enabled.ts
var reference_num_report_enabled_exports = {};
__export(reference_num_report_enabled_exports, {
  definition: () => definition43,
  schema: () => schema37
});
import { z as z38 } from "zod";
var schema37 = z38.boolean();
var definition43 = define({
  key: "reference_num_report_enabled",
  schema: schema37,
  label: "Missing Ref Report Enabled",
  description: "Enable the missing reference number report feature for clients at this level.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.REFERENCE_NUMBER.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: false
});

// src/definitions/reference-num-report-label.ts
var reference_num_report_label_exports = {};
__export(reference_num_report_label_exports, {
  definition: () => definition44,
  schema: () => schema38
});
import { z as z39 } from "zod";
var schema38 = z39.string();
var definition44 = define({
  key: "reference_num_report_label",
  schema: schema38,
  label: "Missing Ref Report Label",
  description: "A client-specific label to display for the missing reference number report.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.REFERENCE_NUMBER.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: "Missing Ref Report"
});

// src/definitions/reporting.ts
var reporting_exports = {};
__export(reporting_exports, {
  ReportingAppointmentsTabSchema: () => ReportingAppointmentsTabSchema,
  ReportingExecutiveTabSchema: () => ReportingExecutiveTabSchema,
  ReportingOperationsTabSchema: () => ReportingOperationsTabSchema,
  definition: () => definition45,
  schema: () => schema39
});
import { z as z40 } from "zod";
var ReportingOperationsTabSchema = z40.object({
  enabled: z40.boolean().describe("Show the Operations Overview tab").optional(),
  metrics: z40.object({
    total_moves: z40.boolean().optional(),
    completed: z40.boolean().optional(),
    pending: z40.boolean().optional(),
    canceled: z40.boolean().optional(),
    on_time_pct: z40.boolean().optional(),
    avg_distance: z40.boolean().optional(),
    active_drivers: z40.boolean().optional(),
    moves_per_driver: z40.boolean().optional(),
    repeat_customers: z40.boolean().optional(),
    missing_ref: z40.boolean().optional(),
    rideshares_utilized: z40.boolean().optional()
  }).strict().describe("Per-metric-card visibility on the Operations Overview tab").optional(),
  charts: z40.object({
    move_volume: z40.boolean().optional(),
    status_distribution: z40.boolean().optional(),
    moves_by_weekday: z40.boolean().optional(),
    moves_by_hour: z40.boolean().optional(),
    driver_activity: z40.boolean().optional(),
    distance_distribution: z40.boolean().optional()
  }).strict().describe("Per-chart visibility on the Operations Overview tab").optional()
}).strict();
var ReportingAppointmentsTabSchema = z40.object({
  enabled: z40.boolean().describe("Show the Appointment Usage tab").optional(),
  metrics: z40.object({
    est_utilization: z40.boolean().optional(),
    upcoming_appts: z40.boolean().optional(),
    completed_appts: z40.boolean().optional(),
    canceled_appts: z40.boolean().optional(),
    configured_drivers: z40.boolean().optional(),
    avg_moves_per_driver: z40.boolean().optional(),
    unused_slots: z40.boolean().optional(),
    avg_move_duration: z40.boolean().optional()
  }).strict().describe("Per-metric-card visibility on the Appointment Usage tab").optional(),
  charts: z40.object({
    daily_utilization: z40.boolean().optional(),
    booked_vs_capacity: z40.boolean().optional(),
    appt_volume_by_hour: z40.boolean().optional(),
    driver_activity: z40.boolean().optional()
  }).strict().describe("Per-chart visibility on the Appointment Usage tab").optional()
}).strict();
var ReportingExecutiveTabSchema = z40.object({
  enabled: z40.boolean().describe("Show the Executive KPIs tab").optional(),
  kpis: z40.object({
    trips_assigned: z40.boolean().optional(),
    trips_completed: z40.boolean().optional(),
    trips_validated: z40.boolean().optional()
  }).strict().describe("Per-KPI-gauge visibility on the Executive KPIs tab").optional(),
  charts: z40.object({
    kpi_trend: z40.boolean().optional(),
    validation_breakdown: z40.boolean().optional()
  }).strict().describe("Per-chart visibility on the Executive KPIs tab").optional()
}).strict();
var schema39 = z40.object({
  enabled: z40.boolean().describe("Master gate for the reporting dashboard. When false the dashboard is hidden entirely.").optional(),
  tabs: z40.object({
    operations: ReportingOperationsTabSchema.optional(),
    appointments: ReportingAppointmentsTabSchema.optional(),
    executive: ReportingExecutiveTabSchema.optional()
  }).strict().describe("Per-tab and per-module visibility for the reporting dashboard").optional()
}).strict().describe(
  "Controls the customer-facing reporting dashboard in the dealer portal: a master on/off gate plus per-tab and per-module (metric, chart, KPI) visibility flags. Modules default to visible when their flag is absent."
);
var definition45 = define({
  key: "reporting",
  schema: schema39,
  label: "Reporting",
  description: "Controls the customer-facing reporting dashboard in the dealer portal: a master on/off gate plus per-tab and per-module visibility flags.",
  warning: "This config is replaced wholesale at each config level (shallow top-level merge), not deep-merged. A level that sets `reporting` must store the complete object \u2014 partial overrides at a higher level discard the lower level entirely.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.REPORTING.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: {
    enabled: false,
    tabs: {
      operations: {
        enabled: true,
        metrics: {
          total_moves: true,
          completed: true,
          pending: true,
          canceled: true,
          on_time_pct: true,
          avg_distance: true,
          active_drivers: true,
          moves_per_driver: true,
          repeat_customers: true,
          missing_ref: true,
          rideshares_utilized: true
        },
        charts: {
          move_volume: true,
          status_distribution: true,
          moves_by_weekday: true,
          moves_by_hour: true,
          driver_activity: true,
          distance_distribution: true
        }
      },
      appointments: {
        enabled: true,
        metrics: {
          est_utilization: true,
          upcoming_appts: true,
          completed_appts: true,
          canceled_appts: true,
          configured_drivers: true,
          avg_moves_per_driver: true,
          unused_slots: true,
          avg_move_duration: true
        },
        charts: {
          daily_utilization: true,
          booked_vs_capacity: true,
          appt_volume_by_hour: true,
          driver_activity: true
        }
      },
      executive: {
        enabled: true,
        kpis: {
          trips_assigned: true,
          trips_completed: true,
          trips_validated: true
        },
        charts: {
          kpi_trend: true,
          validation_breakdown: true
        }
      }
    }
  }
});

// src/definitions/serviceable-radius-miles.ts
var serviceable_radius_miles_exports = {};
__export(serviceable_radius_miles_exports, {
  definition: () => definition46,
  schema: () => schema40
});
import { z as z41 } from "zod";
var schema40 = z41.union([z41.number(), z41.string()]);
var definition46 = define({
  key: "serviceable_radius_miles",
  schema: schema40,
  label: "HopDrive Service Radius",
  description: "Used to validate move eligibility within the radius for clients who pre-verify using our API. For more information, see our API documentation here.",
  warning: "If set, must enable pickup or delivery verification.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.SERVICE_RADIUS.key,
  writableAtLevels: [LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN]
});

// src/definitions/customer-serviceable-radius-miles.ts
var customer_serviceable_radius_miles_exports = {};
__export(customer_serviceable_radius_miles_exports, {
  definition: () => definition47,
  schema: () => schema41
});
import { z as z42 } from "zod";
var schema41 = z42.number().nonnegative();
var definition47 = define({
  key: "customer_serviceable_radius_miles",
  schema: schema41,
  label: "Customer Serviceable Radius Miles",
  description: "Used to validate move eligibility within the radius for clients who use our HopDrive Lite tier. Regions do not apply to these customers as they do not utilize our driver network.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.SERVICE_RADIUS.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: 100
});

// src/definitions/serviceable-radius-by-delivery.ts
var serviceable_radius_by_delivery_exports = {};
__export(serviceable_radius_by_delivery_exports, {
  definition: () => definition48,
  schema: () => schema42
});
import { z as z43 } from "zod";
var schema42 = z43.boolean();
var definition48 = define({
  key: "serviceable_radius_by_delivery",
  schema: schema42,
  label: "Delivery Verification for HopDrive Service Radius",
  description: "Toggle on to verify that the delivery address is within the HopDrive Service Radius.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.SERVICE_RADIUS.key,
  writableAtLevels: [LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN]
});

// src/definitions/serviceable-radius-by-pickup.ts
var serviceable_radius_by_pickup_exports = {};
__export(serviceable_radius_by_pickup_exports, {
  definition: () => definition49,
  schema: () => schema43
});
import { z as z44 } from "zod";
var schema43 = z44.boolean();
var definition49 = define({
  key: "serviceable_radius_by_pickup",
  schema: schema43,
  label: "Pickup Verification for HopDrive Service Radius",
  description: "Toggle on to verify that the pickup address is within the HopDrive Service Radius.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.SERVICE_RADIUS.key,
  writableAtLevels: [LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN]
});

// src/definitions/ride-type-override.ts
var ride_type_override_exports = {};
__export(ride_type_override_exports, {
  RideTypeOverrideSchema: () => RideTypeOverrideSchema,
  definition: () => definition50,
  schema: () => schema44
});
import { z as z45 } from "zod";
var RideTypeOverrideSchema = z45.enum(["", "lyft", "uber", "shuttle"]);
var schema44 = RideTypeOverrideSchema;
var definition50 = define({
  key: "ride_type_override",
  schema: schema44,
  label: "Preferred Ride Partner",
  description: "Select the preferred rideshare service for clients at this level. When planning an 'auto' ride, this partner will be selected by default",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.RIDESHARE.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.REGION, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: ""
});

// src/definitions/branding.ts
var branding_exports = {};
__export(branding_exports, {
  definition: () => definition51,
  schema: () => schema45
});
import { z as z46 } from "zod";
var schema45 = z46.object({
  active: z46.boolean().describe(
    "Activate/Deactivate the branding feature for clients at this level. Branded clients will be visible to their customers not as HopDrive, but as the client's own brand."
  ).optional(),
  name: z46.string().describe("Name to be displayed to the client's customers.").optional(),
  primary_color: z46.string().regex(/^#[0-9A-Fa-f]{6}$/).describe('Primary brand color in hex format (e.g., "#0d5298"). Used for core UI elements with emphasis.').nullable().optional().or(z46.literal("")),
  secondary_color: z46.string().regex(/^#[0-9A-Fa-f]{6}$/).describe("Secondary/accent brand color in hex format. Used for secondary UI elements and highlights.").nullable().optional().or(z46.literal("")),
  backdrop_color: z46.string().regex(/^#[0-9A-Fa-f]{6}$/).describe("Background color for logo area in hex format. Be sure to contrast this to the logo for visibility.").nullable().optional().or(z46.literal("")),
  logo_url: z46.string().url().describe(
    "The logo is a link to a PNG or SVG file to give clients at this level some imagery to go along with their name & colors. Due to limitations, the maximum file size is 0.5MB/500KB. Please also make sure the image dimensions are at least 40px tall as anything lower will be too low of a resolution. Stored in hopdrive-branding-logos S3 bucket."
  ).optional().or(z46.literal("")),
  customer_support_phone_number: z46.string().describe(
    "Customer support phone for HopDrive Lite tier ONLY. Displayed in concierge SMS to consumers. Not applicable to Standard tier."
  ).optional()
}).strict().describe(
  "The branding module is a way for us to white-label our clients. White-labeling is a way to display our clients in a unique way compared to the HopDrive default branding."
);
var definition51 = define({
  key: "branding",
  schema: schema45,
  label: "Branding",
  description: "The branding module is a way for us to white-label our clients. White-labeling is a way to display our clients in a unique way compared to the HopDrive default branding.",
  warning: "Only available to organizations. Customer branding inherits from parent org.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.BRANDING.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: {
    active: false,
    name: "",
    primary_color: "",
    secondary_color: "",
    backdrop_color: "",
    logo_url: "",
    customer_support_phone_number: ""
  }
});

// src/definitions/appraisals.ts
var appraisals_exports = {};
__export(appraisals_exports, {
  AppraisalMoveTypeSchema: () => AppraisalMoveTypeSchema,
  AppraisalMoveTypes: () => AppraisalMoveTypes,
  AppraisalTimestampSchema: () => AppraisalTimestampSchema,
  definition: () => definition52,
  schema: () => schema46
});
import { z as z47 } from "zod";
var AppraisalMoveTypes = ["concierge", "operations"];
var AppraisalTimestampSchema = z47.object({
  from: z47.string().datetime({ offset: true }).describe('Start date in ISO 8601 format (e.g., "2025-01-01T00:00:00Z")').optional(),
  to: z47.union([z47.string().datetime({ offset: true }), z47.literal("now")]).describe('End date in ISO 8601 format, or "now" to indicate currently active').optional()
}).describe(
  'Timestamp range for appraisal activation. Toggle on sets "from" to month start; toggle off sets "to" to month end.'
);
var AppraisalMoveTypeSchema = z47.enum(AppraisalMoveTypes);
var schema46 = z47.object({
  active: z47.boolean().describe(
    'Deprecated (card #2346). Appraisals is a product: an active contract carrying a trigger with product_key "appraisals" is what grants it, and nothing reads this flag to decide whether a customer has Appraisals. Kept so stored config rows still render and stay editable. Use Add Product on the contract instead.'
  ).optional(),
  active_timestamps: z47.array(AppraisalTimestampSchema).describe(
    "Deprecated (card #2346). These windows bounded activation when the flag above was the entitlement. A contract carries its own begin and end dates now, and the product service anchors history on the expired contract and the latest appraisal instead. Kept so stored config rows still render."
  ).optional(),
  allowed_move_types: z47.array(AppraisalMoveTypeSchema).describe(
    "Move types that support appraisals. Only these types will have appraisal functionality. Generally, we should allow all move types unless specified in the client contract."
  ).optional(),
  level: z47.number().int().min(0).describe("Appraisal level/tier (0 = default). Higher levels may unlock additional features.").optional()
}).strict().describe(
  "The appraisals module is a way for our clients to appraise vehicles right off the lot. This system is powered by the Blackbook API and allows the client to build their own appraisals based on vehicle trim and condition."
);
var definition52 = define({
  key: "appraisals",
  schema: schema46,
  label: "Appraisals",
  description: "The appraisals module is a way for our clients to appraise vehicles right off the lot. This system is powered by the Blackbook API and allows the client to build their own appraisals based on vehicle trim and condition.",
  warning: "Requires premium subscription. Check client contract before activating.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.APPRAISALS.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.CONTRACT],
  // Card #1184: dealer-visible on the Appraisals page and READ-ONLY there, which is the
  // whole point of including it. It is the worked example of the readable-but-not-writable
  // rendering path.
  readableByRoles: [ROLES.ADMIN, ROLES.DEALER_ADMIN, ROLES.DEALER_SUPER_ADMIN],
  // Card #1323: the read-only intent is now stated, not merely implied.
  //
  // Before this card the only thing keeping a dealer out was the absence of CUSTOMER from
  // writableAtLevels above, one edit away from silently handing dealers a write to a
  // commercial structure. Appraisals is configured against the rate rule group, so the
  // dealer read-only is a decision about WHO, not about where. Saying so means it survives
  // a future level widening: add CUSTOMER above and admin gains the write, dealers do not.
  writableByRoles: [ROLES.ADMIN],
  defaultValue: {
    active: false,
    active_timestamps: [],
    allowed_move_types: AppraisalMoveTypes,
    level: 0
  }
});

// src/definitions/appointments.ts
var appointments_exports = {};
__export(appointments_exports, {
  definition: () => definition53,
  schema: () => schema47
});
import { z as z48 } from "zod";
var schema47 = z48.object({
  enabled: z48.boolean().describe(
    "LEGACY: Enable/disable the appointments feature for clients at this level. When enabled, the appointments feature will be available in the Portal."
  ).optional(),
  ref_num_label: z48.string().describe("LEGACY: Reference number label within the appointments feature.").optional(),
  notificationEmail: z48.string().email().describe("LEGACY: Email address that receives appointment notifications and alerts.").optional()
}).strict().describe("LEGACY: Appointments feature config. For new scheduling, see Appointment Management.");
var definition53 = define({
  key: "appointments",
  schema: schema47,
  label: "Appointments",
  description: "LEGACY: Appointments feature config. For new scheduling, see Appointment Management.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.APPOINTMENTS.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: {
    enabled: false,
    ref_num_label: "Reference ID",
    notificationEmail: ""
  },
  deprecated: true
});

// src/definitions/appointment-management.ts
var appointment_management_exports = {};
__export(appointment_management_exports, {
  definition: () => definition54,
  schema: () => schema48
});
import { z as z49 } from "zod";
var schema48 = z49.object({
  enabled: z49.boolean().describe(
    "Enable or disable appointment management features for clients at this level. When enabled, users can view the timeline in Portal and manage driver schedules. When disabled, all appointment-related functionality will be inactive."
  ).optional(),
  full_planning_enabled: z49.boolean().describe(
    "Enable full planning and time suggestions for appointment management. When enabled, customers can take advantage of all planning features and intelligent time slot recommendations offered by the appointment management system. Note: Appointment Management must be enabled first."
  ).optional(),
  strategy: z49.string().describe(
    "Select the algorithm to use for appointment scheduling. Each algorithm has different scheduling criteria and driver utilization logic."
  ).optional(),
  driver_count: z49.number().int().positive().describe(
    "For Gig customers only: Expected number of drivers typically available for this rooftop. Used by the algorithm to calculate capacity and suggest appropriate appointment times. For SaaS customers, the system reads from their actual drivers."
  ).optional(),
  move_buffer_minutes: z49.number().int().nonnegative().describe(
    "Minimum time gap (in minutes) required between consecutive moves. Prevents back-to-back scheduling that doesn't account for traffic delays, customer interaction time, and vehicle inspection/handoff. Example: 40 minutes ensures drivers have breathing room between appointments."
  ).optional(),
  enforcement_mode: z49.enum(["suggestion", "forced"]).describe(
    'Controls whether suggested times are recommendations or hard constraints. "Suggestion" displays suggested times but allows dealer to override. "Forced" only allows suggested times to be selectable, preventing submission of non-compliant appointment times.'
  ).optional(),
  offset_max_minutes: z49.number().int().nonnegative().describe(
    "Maximum time deviation (in minutes) a dealer can adjust from a suggested appointment time. Controls how far dealers can shift appointments from algorithm-suggested slots. Example: 30-minute offset with 8:00 AM suggested slot allows 7:30 AM (30 minutes earlier), 8:00 AM (suggested time), or 8:30 AM (30 minutes later)."
  ).optional(),
  offset_increment_minutes: z49.number().int().refine((v) => [5, 10, 15].includes(v)).describe(
    "Defines the time increment (in minutes) for allowable offset adjustments. Prevents arbitrary times like 8:12 AM or 8:37 AM that complicate scheduling. Works in conjunction with Offset Max to control available override options. Example: 15-minute increments allow 8:00, 8:15, 8:30, 8:45, etc."
  ).optional()
}).strict().describe(
  "Configure how appointment times are calculated, presented, and enforced for dealers. Control driver utilization algorithms, time buffers, enforcement modes, and allowable scheduling adjustments."
);
var definition54 = define({
  key: "appointment_management",
  schema: schema48,
  label: "Appointment Management",
  description: "Configure how appointment times are calculated, presented, and enforced for dealers. Control driver utilization algorithms, time buffers, enforcement modes, and allowable scheduling adjustments.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.APPOINTMENT_MANAGEMENT.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: {
    enabled: false,
    full_planning_enabled: true,
    strategy: "balance-drivers-v1",
    driver_count: 2,
    move_buffer_minutes: 15,
    enforcement_mode: "suggestion",
    offset_max_minutes: 15,
    offset_increment_minutes: 15
  }
});

// src/definitions/hopdrive-contacts.ts
var hopdrive_contacts_exports = {};
__export(hopdrive_contacts_exports, {
  definition: () => definition55,
  schema: () => schema49
});
import { z as z50 } from "zod";
var schema49 = z50.object({
  account_manager: z50.number().int().describe("Account manager user ID. Primary point of contact for day-to-day account needs.").nullable().optional(),
  ar_rep: z50.number().int().describe("Accounts Receivable representative user ID. Handles billing inquiries and payment issues.").nullable().optional(),
  sales_rep: z50.number().int().describe("Sales representative user ID. Primary contact for upselling and account expansion.").nullable().optional(),
  market_manager: z50.number().int().describe("Market manager user ID. Oversees regional operations and driver relationships.").nullable().optional()
}).strict().describe("HopDrive employee assignments. Internal team members responsible for this account relationship.");
var definition55 = define({
  key: "hopdrive_contacts",
  schema: schema49,
  label: "HopDrive Contacts",
  description: "HopDrive employee assignments. Internal team members responsible for this account relationship.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.HOPDRIVE_CONTACTS.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.REGION, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: {
    account_manager: null,
    ar_rep: null,
    sales_rep: null,
    market_manager: null
  }
});

// src/definitions/fuel.ts
var fuel_exports = {};
__export(fuel_exports, {
  definition: () => definition56,
  schema: () => schema50
});
import { z as z51 } from "zod";
var schema50 = z51.object({
  fuel_level: z51.number().min(0).max(1).describe("The expected fuel level at vehicle delivery (in percent).").optional(),
  min_fuel_price: z51.number().nonnegative().describe("Minimum fuel spend in dollars. Amounts below this will NOT be authorized. Prevents trivial purchases.").optional(),
  max_fuel_price: z51.number().nonnegative().describe(
    "Maximum fuel spend in dollars. Amounts above this will NOT be authorized at pump. Prevents excessive purchases."
  ).optional(),
  min_fuel_spend: z51.number().nonnegative().describe(
    "LEGACY: Alternative minimum fuel spend threshold. DEPRECATED: Use min_fuel_price instead. Still used in fuel-sdk, event-handlers, hasura-migrations. At least one production customer (ID 3) still uses this field. Both min_fuel_spend and min_fuel_price are supported for backwards compatibility."
  ).optional(),
  max_fuel_spend: z51.number().nonnegative().describe(
    "LEGACY: Alternative maximum fuel spend cap. DEPRECATED: Use max_fuel_price instead. Still used in fuel-sdk, event-handlers, hasura-migrations. Both max_fuel_spend and max_fuel_price are supported for backwards compatibility."
  ).optional()
}).strict().describe(
  "Fuel reimbursement and authorization configuration. Controls driver fuel spending limits and required fuel levels."
);
var definition56 = define({
  key: "fuel",
  schema: schema50,
  label: "Fuel",
  description: "Fuel reimbursement and authorization configuration. Controls driver fuel spending limits and required fuel levels.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.FUEL.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: {
    fuel_level: 0.0625,
    min_fuel_price: 5,
    max_fuel_price: 150
  }
});

// src/definitions/fraud-audit.ts
var fraud_audit_exports = {};
__export(fraud_audit_exports, {
  definition: () => definition57,
  schema: () => schema51
});
import { z as z52 } from "zod";
var schema51 = z52.object({
  enabled: z52.boolean().describe(
    "Turn on fraud audit monitoring to detect potential fraud cases. This will flag any trips that are outside the configured distance and time tolerances."
  ).optional(),
  time_tolerance: z52.number().int().min(0).max(120).describe("Maximum allowed time deviation (in minutes) before flagging as potentially fraudulent behavior.").optional(),
  distance_tolerance: z52.number().int().min(0).max(50).describe("Maximum allowed distance deviation (in miles) before flagging as potentially fraudulent behavior.").optional()
}).strict().describe(
  "Fraud audit settings help detect potential fraud cases. This will flag any trips that are outside the configured distance and time tolerances."
);
var definition57 = define({
  key: "fraud_audit",
  schema: schema51,
  label: "Fraud Audit",
  description: "Fraud audit settings help detect potential fraud cases. This will flag any trips that are outside the configured distance and time tolerances.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.FRAUD_AUDIT.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.REGION, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: { enabled: false, time_tolerance: 15, distance_tolerance: 5 }
});

// src/definitions/gps-transition-mode.ts
var gps_transition_mode_exports = {};
__export(gps_transition_mode_exports, {
  definition: () => definition58,
  schema: () => schema52
});
import { z as z53 } from "zod";
var schema52 = z53.enum(["shadow", "live"]);
var definition58 = define({
  key: "gps_transition_mode",
  schema: schema52,
  label: "GPS Transition Mode",
  description: "How the server treats GPS-proposed move status transitions. In shadow mode every proposal is evaluated and logged to gps_transition_log but the move is never changed; in live mode accepted proposals update the move status and timestamp. Leave on shadow until the comparison data shows the engine agrees with driver taps.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.GPS_MOVE_STATUS.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.REGION, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN, ROLES.DRIVER],
  defaultValue: "shadow"
});

// src/definitions/arrival-policy-arrive.ts
var arrival_policy_arrive_exports = {};
__export(arrival_policy_arrive_exports, {
  definition: () => definition59,
  schema: () => schema53
});
var schema53 = transitionPolicySchema;
var definition59 = define({
  key: "arrival_policy_arrive",
  schema: schema53,
  label: "Arrival Policy: Arrive",
  description: "How a driver arrives at a stop (pickup arrived, delivery arrived). The GPS transition engine applies a corroborated arrival only when this mode allows automation. " + TRANSITION_POLICY_DESCRIPTION,
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.GPS_MOVE_STATUS.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.REGION, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN, ROLES.DRIVER],
  defaultValue: DEFAULT_TRANSITION_POLICY
});

// src/definitions/arrival-policy-depart.ts
var arrival_policy_depart_exports = {};
__export(arrival_policy_depart_exports, {
  definition: () => definition60,
  schema: () => schema54
});
var schema54 = transitionPolicySchema;
var definition60 = define({
  key: "arrival_policy_depart",
  schema: schema54,
  label: "Arrival Policy: Depart",
  description: "How a driver departs a stop. Independent of the arrive policy. For drive-service moves the GPS transition engine never applies a departure regardless of mode, because pickup successful also means the inspection and VIN capture are done; the mode is still recorded with every departure decision. " + TRANSITION_POLICY_DESCRIPTION,
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.GPS_MOVE_STATUS.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.REGION, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN, ROLES.DRIVER],
  defaultValue: DEFAULT_TRANSITION_POLICY
});

// src/definitions/pickup-start-gate.ts
var pickup_start_gate_exports = {};
__export(pickup_start_gate_exports, {
  definition: () => definition61,
  schema: () => schema55
});
import { z as z54 } from "zod";
var schema55 = z54.enum(["off", "soft", "force"]);
var definition61 = define({
  key: "pickup_start_gate",
  schema: schema55,
  label: "Pickup Start Gate",
  description: "Whether the driver app gates Start Pickup on the pickup geofence. off: no gate, the Start control is always available. soft: outside the fence the app shows Head to Pickup with the distance; the driver can start anyway immediately, and the start is logged as a force start. force: as soft, but starting anyway requires a countdown and an acknowledgement. Force starts are logged as move.pickup.force_started either way.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.GPS_MOVE_STATUS.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.REGION, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN, ROLES.DRIVER],
  defaultValue: "off"
});

// src/definitions/gps-optimistic-arrivals.ts
var gps_optimistic_arrivals_exports = {};
__export(gps_optimistic_arrivals_exports, {
  definition: () => definition62,
  schema: () => schema56
});
import { z as z55 } from "zod";
var schema56 = z55.boolean();
var definition62 = define({
  key: "gps_optimistic_arrivals",
  schema: schema56,
  label: "Optimistic GPS Arrivals",
  description: "Whether the driver app advances a move to pickup arrived / delivery arrived on its own when the phone enters the stop geofence, instead of waiting for the driver to swipe. Arrivals only; the departure from pickup has its own key (gps_optimistic_departures) and every other step stays with the driver. Respects arrival_policy_arrive (manualOnly disables it). The server ignores this key: its transition engine still evaluates and logs the same crossing under gps_transition_mode, so the shadow report can compare the two.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.GPS_MOVE_STATUS.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.REGION, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN, ROLES.DRIVER],
  defaultValue: false
});

// src/definitions/gps-optimistic-departures.ts
var gps_optimistic_departures_exports = {};
__export(gps_optimistic_departures_exports, {
  definition: () => definition63,
  schema: () => schema57
});
import { z as z56 } from "zod";
var schema57 = z56.boolean();
var definition63 = define({
  key: "gps_optimistic_departures",
  schema: schema57,
  label: "Optimistic GPS Departures",
  description: "Whether the driver app advances a move from pickup successful to delivery started on its own when the phone leaves the pickup geofence, instead of waiting for the driver to swipe Start Delivery. Departure from pickup only; the pickup inspection still sets pickup successful. Respects arrival_policy_depart (manualOnly disables it). The server ignores this key: its transition engine still evaluates and logs the same crossing under gps_transition_mode.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.GPS_MOVE_STATUS.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.REGION, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN, ROLES.DRIVER],
  defaultValue: false
});

// src/definitions/geofence-default-radius-meters.ts
var geofence_default_radius_meters_exports = {};
__export(geofence_default_radius_meters_exports, {
  definition: () => definition64,
  schema: () => schema58
});
import { z as z57 } from "zod";
var schema58 = z57.number().int().min(50).max(5e3);
var definition64 = define({
  key: "geofence_default_radius_meters",
  schema: schema58,
  label: "Geofence Default Radius (m)",
  description: "Radius in meters of the circular geofence used around a pickup or delivery location when the location has no custom geofence radius or polygon.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.GPS_GEOFENCE_EVIDENCE.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.REGION, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN, ROLES.DRIVER],
  defaultValue: 200
});

// src/definitions/geofence-corroboration-window-minutes.ts
var geofence_corroboration_window_minutes_exports = {};
__export(geofence_corroboration_window_minutes_exports, {
  definition: () => definition65,
  schema: () => schema59
});
import { z as z58 } from "zod";
var schema59 = z58.number().int().min(1).max(60);
var definition65 = define({
  key: "geofence_corroboration_window_minutes",
  schema: schema59,
  label: "Geofence Corroboration Window (min)",
  description: "How many minutes of GPS pings before a proposed transition the server looks at when checking that the driver really was inside (or outside) the geofence.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.GPS_GEOFENCE_EVIDENCE.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.REGION, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN, ROLES.DRIVER],
  defaultValue: 5
});

// src/definitions/geofence-corroboration-min-pings.ts
var geofence_corroboration_min_pings_exports = {};
__export(geofence_corroboration_min_pings_exports, {
  definition: () => definition66,
  schema: () => schema60
});
import { z as z59 } from "zod";
var schema60 = z59.number().int().min(1).max(20);
var definition66 = define({
  key: "geofence_corroboration_min_pings",
  schema: schema60,
  label: "Geofence Corroboration Minimum Pings",
  description: "Minimum number of recent GPS pings that must fall inside the target geofence (outside it for a departure) before the server accepts a GPS-proposed transition.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.GPS_GEOFENCE_EVIDENCE.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.REGION, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN, ROLES.DRIVER],
  defaultValue: 3
});

// src/definitions/geofence-corroboration-accuracy-cutoff-meters.ts
var geofence_corroboration_accuracy_cutoff_meters_exports = {};
__export(geofence_corroboration_accuracy_cutoff_meters_exports, {
  definition: () => definition67,
  schema: () => schema61
});
import { z as z60 } from "zod";
var schema61 = z60.number().int().min(10).max(1e3);
var definition67 = define({
  key: "geofence_corroboration_accuracy_cutoff_meters",
  schema: schema61,
  label: "Geofence Corroboration Accuracy Cutoff (m)",
  description: "GPS pings whose reported accuracy is worse than this many meters are ignored when the server corroborates a transition. Server-side geofence evidence only; the driver app's route monitoring has its own cutoff (route_gps_accuracy_cutoff).",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.GPS_GEOFENCE_EVIDENCE.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.REGION, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN, ROLES.DRIVER],
  defaultValue: 100
});

// src/definitions/geofence-corroboration-drift-buffer-meters.ts
var geofence_corroboration_drift_buffer_meters_exports = {};
__export(geofence_corroboration_drift_buffer_meters_exports, {
  definition: () => definition68,
  schema: () => schema62
});
import { z as z61 } from "zod";
var schema62 = z61.number().int().min(0).max(1e3);
var definition68 = define({
  key: "geofence_corroboration_drift_buffer_meters",
  schema: schema62,
  label: "Geofence Corroboration Drift Buffer (m)",
  description: "Extra meters added to a circular geofence radius when corroborating a transition, to absorb GPS drift. Polygon geofences are checked exactly.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.GPS_GEOFENCE_EVIDENCE.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.REGION, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN, ROLES.DRIVER],
  defaultValue: 100
});

// src/definitions/gps-stale-pickup-started-minutes.ts
var gps_stale_pickup_started_minutes_exports = {};
__export(gps_stale_pickup_started_minutes_exports, {
  definition: () => definition69,
  schema: () => schema63
});
import { z as z62 } from "zod";
var schema63 = z62.number().int().min(5).max(1440);
var definition69 = define({
  key: "gps_stale_pickup_started_minutes",
  schema: schema63,
  label: "Stale Threshold: Pickup Started (min)",
  description: "Minutes a move may sit in pickup started before the stale-move cron evaluates its GPS trail for a missed arrival.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.GPS_STALE_MOVES.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.REGION],
  readableByRoles: [ROLES.ADMIN, ROLES.DRIVER],
  defaultValue: 120
});

// src/definitions/gps-stale-pickup-arrived-minutes.ts
var gps_stale_pickup_arrived_minutes_exports = {};
__export(gps_stale_pickup_arrived_minutes_exports, {
  definition: () => definition70,
  schema: () => schema64
});
import { z as z63 } from "zod";
var schema64 = z63.number().int().min(5).max(1440);
var definition70 = define({
  key: "gps_stale_pickup_arrived_minutes",
  schema: schema64,
  label: "Stale Threshold: Pickup Arrived (min)",
  description: "Minutes a move may sit in pickup arrived before the stale-move cron evaluates its GPS trail for a missed departure.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.GPS_STALE_MOVES.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.REGION],
  readableByRoles: [ROLES.ADMIN, ROLES.DRIVER],
  defaultValue: 45
});

// src/definitions/gps-stale-pickup-successful-minutes.ts
var gps_stale_pickup_successful_minutes_exports = {};
__export(gps_stale_pickup_successful_minutes_exports, {
  definition: () => definition71,
  schema: () => schema65
});
import { z as z64 } from "zod";
var schema65 = z64.number().int().min(5).max(1440);
var definition71 = define({
  key: "gps_stale_pickup_successful_minutes",
  schema: schema65,
  label: "Stale Threshold: Pickup Successful (min)",
  description: "Minutes a move may sit in pickup successful before the stale-move cron evaluates its GPS trail for a missed delivery start or arrival.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.GPS_STALE_MOVES.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.REGION],
  readableByRoles: [ROLES.ADMIN, ROLES.DRIVER],
  defaultValue: 180
});

// src/definitions/gps-stale-delivery-started-minutes.ts
var gps_stale_delivery_started_minutes_exports = {};
__export(gps_stale_delivery_started_minutes_exports, {
  definition: () => definition72,
  schema: () => schema66
});
import { z as z65 } from "zod";
var schema66 = z65.number().int().min(5).max(1440);
var definition72 = define({
  key: "gps_stale_delivery_started_minutes",
  schema: schema66,
  label: "Stale Threshold: Delivery Started (min)",
  description: "Minutes a move may sit in delivery started before the stale-move cron evaluates its GPS trail for a missed arrival at delivery.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.GPS_STALE_MOVES.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.REGION],
  readableByRoles: [ROLES.ADMIN, ROLES.DRIVER],
  defaultValue: 180
});

// src/definitions/gps-stale-delivery-arrived-minutes.ts
var gps_stale_delivery_arrived_minutes_exports = {};
__export(gps_stale_delivery_arrived_minutes_exports, {
  definition: () => definition73,
  schema: () => schema67
});
import { z as z66 } from "zod";
var schema67 = z66.number().int().min(5).max(1440);
var definition73 = define({
  key: "gps_stale_delivery_arrived_minutes",
  schema: schema67,
  label: "Stale Threshold: Delivery Arrived (min)",
  description: "Minutes a move may sit in delivery arrived before the stale-move cron flags it for review.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.GPS_STALE_MOVES.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.REGION],
  readableByRoles: [ROLES.ADMIN, ROLES.DRIVER],
  defaultValue: 45
});

// src/definitions/route-corridor-off-route.ts
var route_corridor_off_route_exports = {};
__export(route_corridor_off_route_exports, {
  definition: () => definition74,
  schema: () => schema68
});
import { z as z67 } from "zod";
var schema68 = z67.number().int().min(10).max(1e3);
var definition74 = define({
  key: "route_corridor_off_route",
  schema: schema68,
  label: "Route Corridor: Off-Route Distance (m)",
  description: "Distance in meters from the planned route polyline beyond which the driver app records a route deviation.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.ROUTE_MONITORING.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.REGION, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN, ROLES.DRIVER],
  defaultValue: 80
});

// src/definitions/route-deviation-alerts.ts
var route_deviation_alerts_exports = {};
__export(route_deviation_alerts_exports, {
  definition: () => definition75,
  schema: () => schema69
});
import { z as z68 } from "zod";
var schema69 = z68.boolean();
var definition75 = define({
  key: "route_deviation_alerts",
  schema: schema69,
  label: "Route Deviation Alerts",
  description: "Whether the driver app alerts the driver when they leave the planned route while carrying the vehicle: a banner, a haptic, and a notification when the app is in the background. Route deviations are still detected and recorded when this is off; only the driver-facing alert is suppressed.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.ROUTE_MONITORING.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.REGION, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN, ROLES.DRIVER],
  defaultValue: false
});

// src/definitions/route-corridor-rejoin.ts
var route_corridor_rejoin_exports = {};
__export(route_corridor_rejoin_exports, {
  definition: () => definition76,
  schema: () => schema70
});
import { z as z69 } from "zod";
var schema70 = z69.number().int().min(5).max(1e3);
var definition76 = define({
  key: "route_corridor_rejoin",
  schema: schema70,
  label: "Route Corridor: Rejoin Distance (m)",
  description: "Distance in meters from the planned route within which a deviated driver is considered back on route. Kept below the off-route distance so the state does not flap at the boundary.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.ROUTE_MONITORING.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.REGION, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN, ROLES.DRIVER],
  defaultValue: 40
});

// src/definitions/route-gps-accuracy-cutoff.ts
var route_gps_accuracy_cutoff_exports = {};
__export(route_gps_accuracy_cutoff_exports, {
  definition: () => definition77,
  schema: () => schema71
});
import { z as z70 } from "zod";
var schema71 = z70.number().int().min(10).max(1e3);
var definition77 = define({
  key: "route_gps_accuracy_cutoff",
  schema: schema71,
  label: "Route Monitoring: GPS Accuracy Cutoff (m)",
  description: "GPS fixes with reported accuracy worse than this many meters are ignored by route deviation, stop detection and compliance analysis. Does not affect GPS arrivals and departures, whose server-side evidence check has its own cutoff (geofence_corroboration_accuracy_cutoff_meters).",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.ROUTE_MONITORING.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.REGION, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN, ROLES.DRIVER],
  defaultValue: 40
});

// src/definitions/route-stop-dwell-threshold.ts
var route_stop_dwell_threshold_exports = {};
__export(route_stop_dwell_threshold_exports, {
  definition: () => definition78,
  schema: () => schema72
});
import { z as z71 } from "zod";
var schema72 = z71.number().int().min(60).max(7200);
var definition78 = define({
  key: "route_stop_dwell_threshold",
  schema: schema72,
  label: "Unplanned Stop: Dwell Threshold (s)",
  description: "Seconds a driver must remain stopped before the stop counts as an unplanned stop rather than traffic.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.ROUTE_MONITORING.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.REGION, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN, ROLES.DRIVER],
  defaultValue: 420
});

// src/definitions/route-stop-radius.ts
var route_stop_radius_exports = {};
__export(route_stop_radius_exports, {
  definition: () => definition79,
  schema: () => schema73
});
import { z as z72 } from "zod";
var schema73 = z72.number().int().min(10).max(500);
var definition79 = define({
  key: "route_stop_radius",
  schema: schema73,
  label: "Unplanned Stop: Radius (m)",
  description: "Radius in meters within which GPS fixes are treated as the same stop while measuring dwell. Default 100 m matches the driver app's field-tuned anchor radius (the pitch listed 50 m).",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.ROUTE_MONITORING.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.REGION, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN, ROLES.DRIVER],
  defaultValue: 100
});

// src/definitions/route-stop-on-route-distance.ts
var route_stop_on_route_distance_exports = {};
__export(route_stop_on_route_distance_exports, {
  definition: () => definition80,
  schema: () => schema74
});
import { z as z73 } from "zod";
var schema74 = z73.number().int().min(10).max(1e3);
var definition80 = define({
  key: "route_stop_on_route_distance",
  schema: schema74,
  label: "Unplanned Stop: On-Route Distance (m)",
  description: "Stops within this many meters of the planned route are ignored; stops farther away are recorded as unplanned off-route stops.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.ROUTE_MONITORING.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.REGION, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN, ROLES.DRIVER],
  defaultValue: 60
});

// src/definitions/route-compliance-excess-distance.ts
var route_compliance_excess_distance_exports = {};
__export(route_compliance_excess_distance_exports, {
  definition: () => definition81,
  schema: () => schema75
});
import { z as z74 } from "zod";
var schema75 = z74.number().min(1).max(5);
var definition81 = define({
  key: "route_compliance_excess_distance",
  schema: schema75,
  label: "Route Compliance: Excess Distance Ratio",
  description: "Post-move check. Actual driven distance divided by the planned lane distance above this ratio records an excess distance event (1.15 = 15% farther than planned).",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.ROUTE_MONITORING.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.REGION, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN, ROLES.DRIVER],
  defaultValue: 1.15
});

// src/definitions/route-compliance-excess-time.ts
var route_compliance_excess_time_exports = {};
__export(route_compliance_excess_time_exports, {
  definition: () => definition82,
  schema: () => schema76
});
import { z as z75 } from "zod";
var schema76 = z75.number().min(1).max(5);
var definition82 = define({
  key: "route_compliance_excess_time",
  schema: schema76,
  label: "Route Compliance: Excess Time Ratio",
  description: "Post-move check. Actual transit time divided by the planned lane duration above this ratio records an excess time event (1.2 = 20% longer than planned).",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.ROUTE_MONITORING.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.REGION, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN, ROLES.DRIVER],
  defaultValue: 1.2
});

// src/definitions/program-visuals.ts
var program_visuals_exports = {};
__export(program_visuals_exports, {
  definition: () => definition83,
  schema: () => schema77
});
import { z as z76 } from "zod";
var schema77 = z76.object({
  active: z76.boolean().describe("Show the program surfaces in the dealer portal: coverage on the move planner, the program breakdown on invoices, and the Programs page.").optional(),
  show_recoup: z76.boolean().describe('Also show the "you owe the program" line when the program fronts a cost the dealer pays back.').optional()
}).strict().describe("Whether dealers enrolled in a program see its coverage, claims and money in the dealer portal. Billing runs regardless; this only controls what is shown.");
var definition83 = define({
  key: "program_visuals",
  schema: schema77,
  label: "Program Visuals",
  description: "Whether dealers enrolled in a program see its coverage, claims and money in the dealer portal. Set on the program to switch every enrolled dealer at once; set on a customer to override it for that dealer.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.PROGRAMS.key,
  writableAtLevels: [LEVELS.PROGRAM, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN, ROLES.DEALER_ADMIN, ROLES.DEALER],
  defaultValue: { active: false, show_recoup: false }
});

// src/definitions/program-reporting.ts
var program_reporting_exports = {};
__export(program_reporting_exports, {
  REPORT_TYPE_DETAIL: () => REPORT_TYPE_DETAIL,
  definition: () => definition84,
  schema: () => schema78
});
import { z as z77 } from "zod";
var FIRST_RUN_DAY = 1;
var LAST_RUN_DAY = 28;
var MAX_DELAY_DAYS = 27;
var REPORT_TYPE_DETAIL = "detail";
var schema78 = z77.object({
  enabled: z77.boolean().describe("Generate and deliver the program report on the schedule below."),
  timezone: z77.string().min(1).describe("IANA time zone the run days are evaluated in, e.g. America/New_York."),
  run_days: z77.array(z77.number().int().min(FIRST_RUN_DAY).max(LAST_RUN_DAY)).min(1).describe("Days of the month the report runs (1 to 28, so every month has them)."),
  delay_days: z77.number().int().min(0).max(MAX_DELAY_DAYS).describe("How many days after the period closes to wait before reporting it, so late data lands first."),
  report_types: z77.array(z77.string().min(1)).min(1).describe("Which reports to generate on each run.")
}).strict().describe("When the program report is generated for the OEM. Program level only: one schedule covers every enrolled dealer.");
var definition84 = define({
  key: "program_reporting",
  schema: schema78,
  label: "Program Reporting",
  description: "When the program report is generated for the OEM. One schedule covers every enrolled dealer, so this is set on the program only.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.PROGRAMS.key,
  writableAtLevels: [LEVELS.PROGRAM],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: { enabled: false, timezone: "America/New_York", run_days: [FIRST_RUN_DAY], delay_days: 0, report_types: [REPORT_TYPE_DETAIL] }
});

// src/definitions/guestrides-access.ts
var guestrides_access_exports = {};
__export(guestrides_access_exports, {
  definition: () => definition85,
  schema: () => schema79
});
import { z as z78 } from "zod";
var schema79 = z78.object({
  enabled: z78.boolean().describe(
    "Enable or disable the guest ride feature for clients at this level. Guest rides give clients the ability to call rides for their guests or have their guests call their own ride."
  ).optional(),
  credit_limit: z78.number().nonnegative().describe(
    "Maximum outstanding Guest Rides balance in dollars before new rides are restricted. Consumed by the guestrides_credit_v1 product algorithm in @hopdrive/sdk-products, which resolves the product LIMITED (history and settlement stay available, only new rides are blocked). Absent or 0 means no credit gating."
  ).optional(),
  credit_warning_threshold: z78.number().min(0).max(1).describe(
    "Fraction of credit_limit at which the Guest Rides decision starts carrying a creditWarning marker, so a dealer sees trouble coming before new rides are blocked. 0.8 means warn once the outstanding balance reaches 80% of the limit. Consumed by the guestrides_credit_v1 product algorithm in @hopdrive/sdk-products; clients read the marker and never compute the threshold themselves. Absent or 0 disables the warning tier. It never affects the hard limit, which keeps gating at credit_limit either way."
  ).optional(),
  marketable: z78.boolean().describe(
    "Show Guest Rides marketing/discovery content to customers who are not entitled to it. Absent means TRUE (marketing by default, owner decision 2026-08-28 on #1225); set false to hide the product entirely (HIDDEN) for EVERY customer at this scope, entitled ones included. Suppression outranks entitlement in the resolution tree, which is why this is admin-only after #1411. Consumed by @hopdrive/sdk-products."
  ).optional(),
  algorithm_key: z78.string().describe(
    "Key of a product algorithm registered in @hopdrive/sdk-products that refines the default Guest Rides access decision (e.g. guestrides_credit_v1 producing LIMITED from credit exposure). Absent means the default resolution tree stands."
  ).optional()
}).strict().describe(
  "Guest Rides entitlement, credit terms and access algorithm. Admin only: a dealer must not enable their own product or raise their own credit line."
);
var definition85 = define({
  key: "guestrides_access",
  schema: schema79,
  label: "Guest Rides Access",
  description: "Guest Rides entitlement, credit terms and access algorithm. Admin only: a dealer must not enable their own product or raise their own credit line.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.GUESTRIDES.key,
  // The same levels `guestrides` was writable at before the split, so no admin capability moved.
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.CUSTOMER],
  // Card #1411, the whole point of the split. Declared on both cells rather than left to the
  // `writableByRoles ?? readableByRoles` default, so a future widening of either has to be
  // written down here and argued for.
  readableByRoles: [ROLES.ADMIN],
  writableByRoles: [ROLES.ADMIN],
  defaultValue: {
    enabled: false,
    // The warning tier's 0.8 lives here, not as a fallback in the algorithm (#1300).
    // hopdrive_default_config() is a table synced from this package, so this is the value
    // that reaches effective config; code that finds the key absent leaves the warning off
    // rather than inventing a threshold.
    credit_warning_threshold: 0.8
  },
  // Product-access algorithm options for the `algorithm_key` field, sourced
  // from the algorithms catalog filtered to product-access keys (the sdk-products
  // registry is code, not a table, so its selectable keys are mirrored into
  // `public.algorithms` by an idempotent migration; a drift tripwire in
  // sdk-products keeps the two in sync). The host loads these once and hands them
  // to the renderer as `enumOptions`, exactly like ride_provider_algorithm_key.
  storage: {
    enum: {
      from: "algorithms",
      select: {
        key: true,
        label: true,
        description: true
      },
      where: {
        type: { _eq: "product-access" }
      },
      orderBy: { key: "asc" },
      mapping: {
        value: "key",
        label: "label",
        description: "description"
      }
    }
  }
});

// src/definitions/product-history.ts
var product_history_exports = {};
__export(product_history_exports, {
  definition: () => definition86,
  schema: () => schema80
});
import { z as z79 } from "zod";
var schema80 = z79.object({
  retention_days: z79.number().int().min(0).describe(
    "Days after a product's last order or its enablement ending during which the portal still shows the product as history-only. Past the window the product is advertised (or hidden when suppressed). A product under an active contract stays history-only regardless of this window."
  ).optional()
}).strict().describe("The history window: how long a product with no active enablement stays visible as history-only in the customer portals.");
var definition86 = define({
  key: "product_history",
  schema: schema80,
  label: "Product History Window",
  description: "How long a product with no active enablement stays visible as history-only in the customer portals before it is advertised or hidden.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.PRODUCT_EXPERIENCE.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: {
    retention_days: 90
  }
});

// src/definitions/dms.ts
var dms_exports = {};
__export(dms_exports, {
  definition: () => definition87,
  schema: () => schema81
});
import { z as z80 } from "zod";
var schema81 = z80.object({
  service: z80.string().describe('DMS service provider name (e.g., "CDK", "Reynolds", "DealerTrack")').optional(),
  props: z80.record(z80.unknown()).describe("Additional DMS-specific properties. Structure varies by provider.").optional()
}).strict().describe("Dealer Management System integration. Enables automatic data sync with dealership DMS.");
var definition87 = define({
  key: "dms",
  schema: schema81,
  label: "DMS",
  description: "Dealer Management System integration. Enables automatic data sync with dealership DMS.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.DMS.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: {
    service: "",
    props: {}
  }
});

// src/definitions/email-scrape-config.ts
var email_scrape_config_exports = {};
__export(email_scrape_config_exports, {
  EmailScrapeMappingSchema: () => EmailScrapeMappingSchema,
  EmailScrapePipelineStepSchema: () => EmailScrapePipelineStepSchema,
  definition: () => definition88,
  schema: () => schema82
});
import { z as z81 } from "zod";
var EmailScrapePipelineStepSchema = z81.object({
  ordinal: z81.number().int().min(0).describe("Execution order (0-indexed). Steps run in ascending order.").optional(),
  plugin: z81.enum(["trim", "getMetaContent", "matchThenNextElement", "extractBetween"]).describe(
    "Plugin: trim=whitespace, getMetaContent=meta tag, matchThenNextElement=find+next, extractBetween=markers"
  ).optional(),
  params: z81.record(z81.string()).describe("Plugin-specific parameters as key-value pairs.").optional()
}).describe("Single step in email scraping pipeline.");
var EmailScrapeMappingSchema = z81.object({
  selector: z81.string().describe("CSS selector to locate the element containing the field value.").optional(),
  pipeline: z81.array(EmailScrapePipelineStepSchema).describe("Ordered processing steps to extract and transform the value.").optional()
}).strict().describe("Field mapping for extracting data from emails.");
var schema82 = z81.object({
  name: z81.string().describe("Configuration name for identification.").optional(),
  version: z81.string().describe("Schema version for compatibility tracking.").optional(),
  key: z81.string().describe("Unique key identifier for this configuration.").optional(),
  mappings: z81.object({
    name: EmailScrapeMappingSchema.optional(),
    phone: EmailScrapeMappingSchema.optional(),
    address: EmailScrapeMappingSchema.optional(),
    vehicle: EmailScrapeMappingSchema.optional(),
    vin: EmailScrapeMappingSchema.optional(),
    advisor: EmailScrapeMappingSchema.optional(),
    services: EmailScrapeMappingSchema.optional(),
    dealer_code: EmailScrapeMappingSchema.optional(),
    appointment_time: EmailScrapeMappingSchema.optional(),
    appointment_type: EmailScrapeMappingSchema.optional(),
    arrangement: EmailScrapeMappingSchema.optional(),
    confirmation_code: EmailScrapeMappingSchema.optional()
  }).strict().describe("Field extraction mappings for appointment email data.").optional()
}).strict().describe("Email scraping configuration for automated data extraction from appointment emails.");
var definition88 = define({
  key: "email_scrape_config",
  schema: schema82,
  label: "Email Scrape Config",
  description: "Email scraping configuration for automated data extraction.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.EMAIL_SCRAPING.key,
  writableAtLevels: [LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN]
});

// src/definitions/rc_config.ts
var rc_config_exports = {};
__export(rc_config_exports, {
  REDCAP_SUBSCRIPTION: () => REDCAP_SUBSCRIPTION,
  RcEmailScrapeConfigSchema: () => RcEmailScrapeConfigSchema,
  definition: () => definition89,
  schema: () => schema83
});
import { z as z82 } from "zod";
var RcEmailScrapeConfigSchema = z82.object({
  name: z82.string().optional(),
  version: z82.string().optional(),
  mappings: z82.object({
    tempPass: EmailScrapeMappingSchema.optional()
  }).strict().optional()
}).strict().describe("Email scraping config for extracting temporary passwords from RedCap's sign-in emails.");
var schema83 = z82.object({
  advisor_email: z82.string().email().describe("Service advisor account the integration signs in to RedCap as. Provisioned by onboarding \u2014 usually an `@hd3.io` service address, not a real person.").optional(),
  advisor_name: z82.string().describe("Display name shown in RedCap for the advisor account.").optional(),
  advisor_phone: z82.string().describe("Phone number on the advisor account in RedCap.").optional(),
  advisor_otp_secret: z82.string().describe("One-time-password secret for the advisor sign-in, when RedCap requires two-factor for the account.").optional(),
  driver_email: z82.string().email().describe("Driver account the integration signs in to RedCap as, for the driver-facing side of the portal.").optional(),
  driver_name: z82.string().describe("Display name shown in RedCap for the driver account.").optional(),
  driver_phone: z82.string().describe("Phone number on the driver account in RedCap.").optional(),
  email_scrape_config: RcEmailScrapeConfigSchema.optional(),
  rc_dealer_admin_config_id: z82.number().int().describe("Which RedCap program this customer belongs to \u2014 `12` is Ford, `3` is Lincoln. Set by onboarding; a customer with none is not fully configured.").optional()
}).strict().describe("RedCap portal credentials and program mapping for this customer.");
var REDCAP_SUBSCRIPTION = {
  events: ["move_created", "move_delivery_successful", "move_cancel_failed", "move_cancel_partial"],
  /**
   * Per environment. The integration also honours a `WEBHOOK_TARGET_URL`
   * override, so a row may legitimately point somewhere else — compare, warn,
   * and let a human decide rather than assuming drift.
   */
  targetUrl: {
    test: "https://t.hd3.io/v1/webhook-background",
    prod: "https://hd3.io/v1/webhook-background"
  },
  description: "Triggering RedCap integration",
  version: "1.0.0"
};
var definition89 = define({
  key: "rc_config",
  schema: schema83,
  label: "RedCap Config",
  description: "Credentials the RedCap integration uses to sign in to their portal on this customer's behalf, and which RedCap program the customer belongs to.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.REDCAP.key,
  writableAtLevels: [LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN]
});

// src/definitions/private.ts
var private_exports = {};
__export(private_exports, {
  definition: () => definition90,
  schema: () => schema84
});
import { z as z83 } from "zod";
var schema84 = z83.record(z83.unknown());
var definition90 = define({
  key: "private",
  schema: schema84,
  label: "Private",
  description: "Private/internal config. Freeform structure with dynamic boolean toggles for data sharing.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.PRIVATE.key,
  writableAtLevels: [
    LEVELS.HOPDRIVE,
    LEVELS.REGION,
    LEVELS.ORGANIZATION,
    LEVELS.CUSTOMER,
    LEVELS.WORKFLOWSET,
    LEVELS.CONTRACT,
    LEVELS.MOVE
  ],
  readableByRoles: [ROLES.ADMIN]
});

// src/definitions/routing.ts
var routing_exports = {};
__export(routing_exports, {
  definition: () => definition91,
  schema: () => schema85
});
import { z as z84 } from "zod";
var schema85 = z84.object({
  avoid_tolls: z84.boolean().describe(
    "When true, routes will avoid toll roads where possible. This may result in longer travel times or distances."
  ).optional(),
  avoid_highways: z84.boolean().describe(
    "When true, routes will avoid highways where possible. Useful for vehicles with highway restrictions or customers preferring local roads."
  ).optional(),
  avoid_ferries: z84.boolean().describe(
    "When true, routes will avoid ferries where possible. Useful for customers who cannot use ferry routes."
  ).optional(),
  route_preference: z84.enum(["fastest", "shortest"]).describe(
    "Route optimization preference. 'fastest' optimizes for travel time (default), 'shortest' optimizes for distance."
  ).optional(),
  no_toll_charges: z84.boolean().describe(
    "When true, toll charges are excluded from the transport cost. Lane tolls are billed as $0 in AR. When false, tolls charge normally."
  ).optional()
}).strict().describe(
  "Routing preferences for lane enrichment. Controls toll avoidance, highway avoidance, ferry avoidance, and route optimization preferences."
);
var definition91 = define({
  key: "routing",
  schema: schema85,
  label: "Routing",
  description: "Routing preferences for lane enrichment. Controls toll avoidance, highway avoidance, ferry avoidance, and route optimization preferences.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.MOVE_PLANNING.key,
  writableAtLevels: [
    LEVELS.HOPDRIVE,
    LEVELS.REGION,
    LEVELS.ORGANIZATION,
    LEVELS.CUSTOMER,
    LEVELS.WORKFLOWSET,
    LEVELS.CONTRACT
  ],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: {
    avoid_tolls: false,
    avoid_highways: false,
    avoid_ferries: false,
    route_preference: "fastest",
    no_toll_charges: false
  }
});

// src/definitions/connector-triggers.ts
var connector_triggers_exports = {};
__export(connector_triggers_exports, {
  AUTOCOMPLETE_FIELDS: () => AUTOCOMPLETE_FIELDS,
  AuthProviderSchema: () => AuthProviderSchema,
  AutocompleteTriggerSchema: () => AutocompleteTriggerSchema,
  CustomHeadersSchema: () => CustomHeadersSchema,
  EnrichmentSchema: () => EnrichmentSchema,
  FETCH_RESULT_FIELDS: () => FETCH_RESULT_FIELDS,
  FetchResultMappingsSchema: () => FetchResultMappingsSchema,
  FetchTriggerSchema: () => FetchTriggerSchema,
  FieldMappingSchema: () => FieldMappingSchema,
  IntegrationSchema: () => IntegrationSchema,
  TransformConfigSchema: () => TransformConfigSchema,
  definition: () => definition92,
  schema: () => schema86
});
import { z as z85 } from "zod";
var transformConfigSchema = z85.discriminatedUnion("type", [
  z85.object({
    type: z85.literal("uppercase")
  }),
  z85.object({
    type: z85.literal("lowercase")
  }),
  z85.object({
    type: z85.literal("trim")
  }),
  z85.object({
    type: z85.literal("regex"),
    config: z85.object({
      pattern: z85.string().describe("Regular expression pattern to match"),
      replacement: z85.string().optional().default("").describe("Replacement string for regex matches")
    })
  }),
  z85.object({
    type: z85.literal("template"),
    config: z85.object({
      template: z85.string().describe("Template string supporting {value} and {fieldName} placeholders")
    })
  }),
  z85.object({
    type: z85.literal("split"),
    config: z85.object({
      delimiter: z85.string().describe("String delimiter to split on"),
      index: z85.number().int().min(0).describe("Index of the split segment to extract")
    })
  }),
  z85.object({
    type: z85.literal("concat"),
    config: z85.object({
      prefix: z85.string().optional().default("").describe("Prefix string to prepend"),
      suffix: z85.string().optional().default("").describe("Suffix string to append")
    })
  }),
  z85.object({
    type: z85.literal("default"),
    config: z85.object({
      value: z85.string().describe("Default value to use if field is empty")
    })
  }),
  z85.object({
    type: z85.literal("date_format"),
    config: z85.object({
      inputFormat: z85.string().optional().describe("Input date format (optional, auto-detected if not provided)"),
      outputFormat: z85.string().describe("Output date format string")
    })
  })
]);
var fieldMappingSchema = z85.object({
  /** JSONPath expression to extract value from API response */
  source: z85.string().describe("JSONPath expression to extract value from API response"),
  /** Optional chain of transforms to apply to the extracted value */
  transforms: z85.array(transformConfigSchema).optional().describe("Optional chain of transforms to apply to the extracted value")
});
var oauth2ClientCredentialsSchema = z85.object({
  type: z85.literal("oauth2_client_credentials"),
  /** OAuth token endpoint URL */
  tokenEndpoint: z85.string().url().describe("OAuth token endpoint URL"),
  /** Client ID - can be a secret reference like {SECRET_NAME} */
  clientId: z85.string().describe("Client ID - can be a secret reference like {SECRET_NAME}"),
  /** Client secret - can be a secret reference like {SECRET_NAME} */
  clientSecret: z85.string().describe("Client secret - can be a secret reference like {SECRET_NAME}"),
  /** OAuth audience (optional) */
  audience: z85.string().optional().describe("OAuth audience (optional)"),
  /** OAuth scope (optional) */
  scope: z85.string().optional().describe("OAuth scope (optional)"),
  /** Token cache TTL in seconds (default: 3540 = 59 minutes) */
  cacheTtlSeconds: z85.number().int().positive().optional().default(3540).describe("Token cache TTL in seconds (default: 3540 = 59 minutes)")
});
var apiKeyAuthSchema = z85.object({
  type: z85.literal("api_key"),
  /** Header name for the API key */
  headerName: z85.string().describe("Header name for the API key"),
  /** API key value - can be a secret reference like {SECRET_NAME} */
  apiKey: z85.string().describe("API key value - can be a secret reference like {SECRET_NAME}")
});
var basicAuthSchema = z85.object({
  type: z85.literal("basic"),
  /** Username - can be a secret reference */
  username: z85.string().describe("Username - can be a secret reference"),
  /** Password - can be a secret reference */
  password: z85.string().describe("Password - can be a secret reference")
});
var authProviderSchema = z85.discriminatedUnion("type", [
  oauth2ClientCredentialsSchema,
  apiKeyAuthSchema,
  basicAuthSchema
]);
var customHeadersSchema = z85.record(z85.string(), z85.string()).describe("Custom headers as key-value pairs. Both header names and values support template variable interpolation.");
var autocompleteTriggerSchema = z85.object({
  /** Whether this trigger is enabled */
  enabled: z85.boolean().describe("Whether this trigger is enabled"),
  /** Which auth provider to use (references key in authProviders) */
  authProvider: z85.string().describe("Which auth provider to use (references key in authProviders)"),
  /** HTTP method for the API call */
  httpMethod: z85.enum(["GET", "POST"]).describe("HTTP method for the API call"),
  /** API endpoint URL - supports {search_query} interpolation */
  endpoint: z85.string().describe("API endpoint URL - supports {search_query} interpolation"),
  /** Request body template for POST requests (optional) */
  requestBody: z85.string().optional().describe("Request body template for POST requests (optional)"),
  /** Optional custom headers to include with the request - supports template variable interpolation */
  headers: customHeadersSchema.optional().default({}).describe(
    'Optional custom headers to include with the request - supports template variable interpolation (e.g., { "api-version": "{cdi.api_version}" })'
  ),
  /** Minimum characters before triggering autocomplete */
  minChars: z85.number().int().min(1).optional().default(3).describe("Minimum characters before triggering autocomplete"),
  /** Debounce delay in milliseconds */
  debounceMs: z85.number().int().min(0).optional().default(300).describe("Debounce delay in milliseconds"),
  /** Maximum results to return */
  maxResults: z85.number().int().min(1).max(50).optional().default(10).describe("Maximum results to return"),
  /** Field mappings for autocomplete output */
  mappings: z85.object({
    /** Unique identifier for the result (required) */
    id: fieldMappingSchema,
    /** Primary display text shown in dropdown (required) */
    displayValue: fieldMappingSchema,
    /** Secondary text shown below display value (optional) */
    subtext: fieldMappingSchema.optional()
  }).describe("Field mappings for autocomplete output")
});
var AUTOCOMPLETE_FIELDS = {
  /** Unique identifier for the result */
  ID: "id",
  /** Primary display text shown in dropdown */
  DISPLAY_VALUE: "displayValue",
  /** Secondary text shown below display value */
  SUBTEXT: "subtext"
};
var FETCH_RESULT_FIELDS = {
  // Identity/Display
  ID: "id",
  DISPLAY_LABEL: "displayLabel",
  // Reference
  REFERENCE_NUMBER: "referenceNumber",
  // Customer Information
  CUSTOMER_NAME: "customerName",
  CUSTOMER_PHONE: "customerPhone",
  CUSTOMER_EMAIL: "customerEmail",
  CUSTOMER_ADDRESS_LINE1: "customerAddressLine1",
  CUSTOMER_ADDRESS_LINE2: "customerAddressLine2",
  CUSTOMER_ADDRESS_CITY: "customerAddressCity",
  CUSTOMER_ADDRESS_STATE: "customerAddressState",
  CUSTOMER_ADDRESS_ZIP: "customerAddressZip",
  // Vehicle Information
  VIN: "vin",
  YEAR: "year",
  MAKE: "make",
  MODEL: "model",
  COLOR: "color",
  STOCK_NUMBER: "stockNumber",
  LICENSE_PLATE: "licensePlate"
};
var fetchResultMappingsSchema = z85.object({
  // Identity/Display
  id: fieldMappingSchema.optional(),
  displayLabel: fieldMappingSchema.optional(),
  // Reference
  referenceNumber: fieldMappingSchema.optional(),
  // Customer Information
  customerName: fieldMappingSchema.optional(),
  customerPhone: fieldMappingSchema.optional(),
  customerEmail: fieldMappingSchema.optional(),
  customerAddressLine1: fieldMappingSchema.optional(),
  customerAddressLine2: fieldMappingSchema.optional(),
  customerAddressCity: fieldMappingSchema.optional(),
  customerAddressState: fieldMappingSchema.optional(),
  customerAddressZip: fieldMappingSchema.optional(),
  // Vehicle Information
  vin: fieldMappingSchema.optional(),
  year: fieldMappingSchema.optional(),
  make: fieldMappingSchema.optional(),
  model: fieldMappingSchema.optional(),
  color: fieldMappingSchema.optional(),
  stockNumber: fieldMappingSchema.optional(),
  licensePlate: fieldMappingSchema.optional()
}).strict();
var enrichmentSchema = z85.object({
  condition: z85.string().optional().describe(
    `JSONPath condition evaluated against the **raw primary API response**.

Enrichment is skipped if the condition returns a falsy value (\`null\`, \`undefined\`, \`""\`, \`0\`, \`false\`, or empty array).

**Condition Styles:**

| Style | Syntax | Behavior |
|-------|--------|----------|
| Simple existence | \`$.id\` | Truthy if field exists and has value |
| Filter expression | \`$[?(@.field == "value")]\` | Truthy if filter matches |

**Filter Expression Examples:**

| Condition | Description |
|-----------|-------------|
| \`$[?(@.tier == "premium")]\` | Tier equals "premium" |
| \`$.vehicle[?(@.year >= 2020)]\` | Vehicle year is 2020 or newer |
| \`$.features[?(@ == "roadside_assistance")]\` | Features array contains value |
| \`$.customer[?(@.membership_level == "gold")]\` | Nested property equals value |
| \`$.vehicles[?(@.primary == true)]\` | Array contains object with primary=true |`
  ),
  authProvider: z85.string().optional().describe(
    `Auth provider to use for this enrichment request.

Defaults to the parent fetch trigger's \`authProvider\` if not specified. Use this to authenticate enrichment calls against a different API than the primary request.`
  ),
  httpMethod: z85.enum(["GET", "POST", "PUT", "PATCH"]).default("GET").describe(
    `HTTP method for the enrichment API call.

Defaults to \`GET\`. Use \`POST\`, \`PUT\`, or \`PATCH\` when the enrichment endpoint requires a request body.`
  ),
  endpoint: z85.string().describe(
    `Endpoint URL with variable interpolation.

**Supported Variables:**

| Variable | Source | Example |
|----------|--------|---------|
| \`{primary.fieldName}\` | Mapped field from primary response | \`{primary.customerId}\` |
| \`{primary_raw.$.path}\` | JSONPath from raw primary response | \`{primary_raw.$.data[0].id}\` |
| \`{lookup_value}\` | Original lookup value | \`{lookup_value}\` |

**Example:**
\`\`\`
https://api.example.com/customers/{primary.customerId}/address
\`\`\`

All interpolated values are URL-encoded automatically.`
  ),
  requestBody: z85.string().optional().describe(
    `Request body template for \`POST\`, \`PUT\`, or \`PATCH\` requests.

Supports the same variable interpolation as \`endpoint\`:
- \`{primary.fieldName}\` - Mapped field from primary response
- \`{primary_raw.$.path}\` - JSONPath from raw primary response
- \`{lookup_value}\` - Original lookup value

**Example:**
\`\`\`json
{"customerId": "{primary.customerId}", "action": "getAddress"}
\`\`\``
  ),
  headers: customHeadersSchema.optional().default({}).describe(
    `Optional custom headers to include with the enrichment request.

Supports the same variable interpolation as \`endpoint\`:
- \`{primary.fieldName}\` - Mapped field from primary response
- \`{primary_raw.$.path}\` - JSONPath from raw primary response
- \`{lookup_value}\` - Original lookup value

**Example:**
\`\`\`json
{ "api-version": "4", "x-tenant-id": "{primary.tenantId}" }
\`\`\``
  ),
  mappings: fetchResultMappingsSchema.describe(
    `Field mappings applied to the enrichment response.

Mapped values are **merged** with the primary response result. Non-null enrichment values will overwrite existing fields from the primary response.

Uses the same JSONPath syntax as primary fetch trigger mappings.`
  )
});
var fetchTriggerSchema = z85.object({
  /** Whether this trigger is enabled */
  enabled: z85.boolean().describe("Whether this trigger is enabled"),
  /** Which auth provider to use (references key in authProviders) */
  authProvider: z85.string().describe("Which auth provider to use (references key in authProviders)"),
  /** HTTP method for the API call */
  httpMethod: z85.enum(["GET", "POST", "PUT", "PATCH"]).describe("HTTP method for the API call"),
  /** API endpoint URL - supports {lookup_value} interpolation */
  endpoint: z85.string().describe("API endpoint URL - supports {lookup_value} interpolation"),
  /** Request body template for POST/PUT/PATCH requests (optional) */
  requestBody: z85.string().optional().describe("Request body template for POST/PUT/PATCH requests (optional)"),
  /** Optional custom headers to include with the request - supports template variable interpolation */
  headers: customHeadersSchema.optional().default({}).describe(
    'Optional custom headers to include with the request - supports template variable interpolation (e.g., { "api-version": "{cdi.api_version}" })'
  ),
  /**
   * Whether fetch results should overwrite existing form field values.
   * When true, all mapped fields from the fetch response will replace existing values.
   * When false (default), only empty form fields will be populated.
   */
  overwriteExisting: z85.boolean().optional().default(false).describe(
    "Whether fetch results should overwrite existing form field values. When true, all mapped fields from the fetch response will replace existing values. When false (default), only empty form fields will be populated."
  ),
  /** Field mappings from 3rd party API response to canonical FetchResult */
  mappings: fetchResultMappingsSchema.describe("Field mappings from 3rd party API response to canonical FetchResult"),
  enrichments: z85.array(enrichmentSchema).optional().default([]).describe(
    `Optional enrichments for chained API calls.

Each enrichment makes a **secondary API call** using data from the primary response, then merges the results into the final \`FetchResult\`.

**Use Cases:**
- Fetch customer address from a separate endpoint
- Get additional details that require the primary record's ID
- Call different APIs conditionally based on primary response data

**Execution Flow:**
1. Primary fetch request executes and maps response
2. Each enrichment's \`condition\` is evaluated against raw primary response
3. Enrichments with passing conditions execute in order
4. Results merge into final response (non-null values overwrite)

**Error Handling:**
Enrichment failures are non-fatal. If an enrichment fails, the primary data is preserved and processing continues with remaining enrichments.`
  )
});
var schema86 = z85.object({
  /** Master enable/disable toggle for the entire integration */
  enabled: z85.boolean().describe("Master enable/disable toggle for the entire integration"),
  /**
   * Encrypted secrets storage.
   * Keys are secret names (e.g., "DEALERWARE_CLIENT_ID")
   * Values are encrypted strings (e.g., "encrypted:v1:abc123...")
   */
  secrets: z85.record(z85.string(), encryptedString).optional().default({}).describe(
    'Encrypted secrets storage. Keys are secret names (e.g., "DEALERWARE_CLIENT_ID"). Values are encrypted strings (e.g., "encrypted:v1:abc123...")'
  ),
  /**
   * Auth provider configurations.
   * Keys are provider names referenced by triggers (e.g., "dealerware-oauth")
   */
  authProviders: z85.record(z85.string(), authProviderSchema).optional().default({}).describe(
    'Auth provider configurations. Keys are provider names referenced by triggers (e.g., "dealerware-oauth")'
  ),
  /**
   * Autocomplete trigger configurations.
   * Keys are trigger types matching form field triggers:
   * - consumer-name
   * - consumer-phone
   * - reference-number
   * - consumer-vin
   * - loaner-vin
   * - stock-number
   */
  autocompleteTriggers: z85.record(z85.string(), autocompleteTriggerSchema).optional().default({}).describe(
    "Autocomplete trigger configurations. Keys are trigger types matching form field triggers: consumer-name, consumer-phone, reference-number, consumer-vin, loaner-vin, stock-number"
  ),
  /**
   * Fetch trigger configurations.
   * Keys are trigger type identifiers (e.g., 'contract-detail', 'consumer-detail', 'vehicle-detail').
   * The trigger type is arbitrary - form code defines what trigger types it fires,
   * and the config defines how to handle each one.
   */
  fetchTriggers: z85.record(z85.string(), fetchTriggerSchema).optional().default({}).describe(
    "Fetch trigger configurations. Keys are trigger type identifiers (e.g., 'contract-detail', 'consumer-detail', 'vehicle-detail'). The trigger type is arbitrary - form code defines what trigger types it fires, and the config defines how to handle each one."
  )
});
var TransformConfigSchema = transformConfigSchema;
var FieldMappingSchema = fieldMappingSchema;
var CustomHeadersSchema = customHeadersSchema;
var AuthProviderSchema = authProviderSchema;
var AutocompleteTriggerSchema = autocompleteTriggerSchema;
var FetchTriggerSchema = fetchTriggerSchema;
var FetchResultMappingsSchema = fetchResultMappingsSchema;
var EnrichmentSchema = enrichmentSchema;
var IntegrationSchema = schema86;
var definition92 = define({
  key: "connector_triggers",
  schema: schema86,
  kind: DEFINITION_KINDS.SETTING,
  writableAtLevels: [LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN, ROLES.DEALER_ADMIN, ROLES.DEALER_SUPER_ADMIN],
  label: "Connector Triggers",
  description: "Configure triggered connectors for move form autocomplete and data fetching from partner APIs",
  category: CATEGORIES.INTEGRATIONS.key
});

// src/definitions/minimum-certification-ids.ts
var minimum_certification_ids_exports = {};
__export(minimum_certification_ids_exports, {
  definition: () => definition93,
  schema: () => schema87
});
import { z as z86 } from "zod";
var schema87 = z86.array(z86.string()).describe("Minimum certification keys all drivers must have in order to receive move offers.");
var definition93 = define({
  key: "minimum_certification_ids",
  kind: DEFINITION_KINDS.SETTING,
  schema: schema87,
  writableAtLevels: [
    LEVELS.REGION,
    LEVELS.ORGANIZATION,
    LEVELS.CUSTOMER,
    LEVELS.WORKFLOWSET,
    LEVELS.CONTRACT,
    LEVELS.MOVE
  ],
  readableByRoles: [ROLES.ADMIN],
  label: "Minimum Certification IDs",
  description: "Minimum certification IDs all drivers must have in order to be receive move offers.",
  category: CATEGORIES.CUSTOM_SETTINGS.key,
  storage: {
    enum: {
      from: "certifications",
      select: {
        key: true,
        name: true,
        description: true,
        requires_documents: true,
        requires_expiration: true,
        notes: true
      },
      orderBy: { name: "asc" },
      mapping: {
        value: "key",
        label: "name",
        description: "description",
        metadata: {
          requires_documents: "requires_documents",
          requires_expiration: "requires_expiration",
          notes: "notes"
        }
      }
    },
    levels: {
      region: {
        type: "link_table",
        table: "regionstocertifications",
        foreignKey: "region_id",
        valueKey: "certification_key",
        auditColumns: {
          createdByColumn: "createdby",
          updatedByColumn: "updatedby",
          createdAtColumn: "created_at",
          updatedAtColumn: "updated_at"
        }
      },
      organization: {
        type: "link_table",
        table: "organizationstocertifications",
        foreignKey: "organization_id",
        valueKey: "certification_key",
        auditColumns: {
          createdByColumn: "createdby",
          updatedByColumn: "updatedby",
          createdAtColumn: "createdat",
          updatedAtColumn: "updated_at"
        }
      },
      customer: {
        type: "link_table",
        table: "customerstocertifications",
        foreignKey: "customer_id",
        valueKey: "certification_key",
        auditColumns: {
          createdByColumn: "createdby",
          updatedByColumn: "updatedby",
          createdAtColumn: "createdat",
          updatedAtColumn: "updated_at"
        }
      }
    }
  },
  defaultValue: []
});

// src/definitions/billing-frequency.ts
var billing_frequency_exports = {};
__export(billing_frequency_exports, {
  definition: () => definition94,
  schema: () => schema88
});
import { z as z87 } from "zod";
var schema88 = z87.enum(["monthly", "weekly", "daily"]);
var definition94 = define({
  key: "billing_frequency",
  kind: DEFINITION_KINDS.SETTING,
  schema: schema88,
  writableAtLevels: [LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
  label: "Billing Frequency",
  description: "Determines how often we generate invoices for this client, which directly affects how often they are billed. This should be set to monthly, unless the client specifically requests otherwise.",
  category: CATEGORIES.BILLING.key,
  defaultValue: "monthly",
  storage: {
    levels: {
      customer: {
        type: "dedicated_column",
        table: "customers",
        column: "billing_frequency"
      }
    }
  }
});

// src/definitions/payment-terms.ts
var payment_terms_exports = {};
__export(payment_terms_exports, {
  definition: () => definition95,
  schema: () => schema89
});
import { z as z88 } from "zod";
var schema89 = z88.number().int().min(1).max(30);
var definition95 = define({
  key: "payment_terms",
  kind: DEFINITION_KINDS.SETTING,
  schema: schema89,
  writableAtLevels: [LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
  label: "Payment Terms",
  description: "Payment terms determine how many days we give the client to pay an invoice after it's end date. This should be set to 30 days, unless the client specifically requests otherwise.",
  category: CATEGORIES.BILLING.key,
  defaultValue: 30,
  storage: {
    levels: {
      customer: {
        type: "dedicated_column",
        table: "customers",
        column: "payment_terms",
        valueType: "numeric"
      }
    }
  }
});

// src/definitions/auto-pay.ts
var auto_pay_exports = {};
__export(auto_pay_exports, {
  definition: () => definition96,
  schema: () => schema90
});
import { z as z89 } from "zod";
var schema90 = z89.boolean();
var definition96 = define({
  key: "auto_pay",
  kind: DEFINITION_KINDS.SETTING,
  schema: schema90,
  writableAtLevels: [LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
  label: "Auto Pay",
  description: "Activate/Deactivate auto-pay for this client. Auto-pay determines whether or not we automatically charge the client's credit card after an invoice closes.",
  category: CATEGORIES.BILLING.key,
  defaultValue: false,
  storage: {
    levels: {
      customer: {
        type: "dedicated_column",
        table: "customers",
        column: "auto_pay",
        valueType: "Boolean"
      }
    }
  }
});

// src/definitions/allow-vinless-valet.ts
var allow_vinless_valet_exports = {};
__export(allow_vinless_valet_exports, {
  definition: () => definition97,
  schema: () => schema91
});
import { z as z90 } from "zod";
var schema91 = z90.boolean();
var definition97 = define({
  key: "allow_vinless_valet",
  schema: schema91,
  label: "Allow Vinless Valet",
  description: "Select whether valet service is permitted without a VIN for customers at this level.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.MOVE_PLANNING.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: false
});

// src/definitions/auto-create-valet-moves.ts
var auto_create_valet_moves_exports = {};
__export(auto_create_valet_moves_exports, {
  definition: () => definition98,
  schema: () => schema92
});
import { z as z91 } from "zod";
var schema92 = z91.boolean();
var definition98 = define({
  key: "auto_create_valet_moves",
  schema: schema92,
  label: "Auto-Create Valet Moves",
  description: "Automatically create a concierge pickup move + scenario when an appointment becomes type=valet. Dispatches a driver to pick up the consumer\u2019s vehicle without manual move planning. Return leg is created separately when service is complete.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.APPOINTMENTS.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN, ROLES.DEALER_ADMIN],
  defaultValue: false
});

// src/definitions/ar.ts
var ar_exports = {};
__export(ar_exports, {
  currencyAmount: () => currencyAmount,
  currencyAmountCents: () => currencyAmountCents,
  definition: () => definition99,
  schema: () => schema93
});
import { z as z92 } from "zod";
var CURRENCY_MIN = 0.01;
var currencyAmount = () => z92.number().positive("Currency amounts MUST be positive and non-zero").min(CURRENCY_MIN, `Currency amounts MUST be \u2265 $${CURRENCY_MIN.toFixed(2)}`).refine((v) => Number((v * 100).toFixed(0)) / 100 === v, {
  message: "Currency amounts MUST have at most 2 decimal places"
}).describe("Currency amount in dollars (e.g. 3.40 means $3.40).");
var currencyAmountCents = currencyAmount;
var serviceEntrySchema = z92.object({
  /**
   * Literal matching the SDK template's `key`. Round-trips with
   * `applyTemplate`'s runtime guard which asserts this equals the
   * applied template's key.
   */
  serviceKey: z92.string().min(1).describe(
    'Template key (e.g. "valet"). MUST match the SDK ServiceTemplate.key it was applied from.'
  ),
  /**
   * Tier variant: `'standard'` (event-based per-move, the Full
   * Service / Traditional variants) vs `'lite'` (SaaS variants —
   * service fee + rideshare platform fee on lite Moves bespokes).
   *
   * Mirrors an existing customer-level `tier` convention so ops can
   * read across customer + contract configs without translating.
   * Extensible — if a third tier emerges (`'premium'`, etc.) the
   * enum extends without restructuring callers.
   */
  tier: z92.enum(["standard", "lite"]).optional().describe(
    "Tier variant. 'standard' = event-based per-use; 'lite' = SaaS variants."
  ),
  /**
   * Frequency for time-based triggers (service fee, lite-tier per-use
   * aggregation). Not consulted for event-based triggers.
   */
  frequency: z92.enum(["weekly", "monthly"]).describe("Frequency for any time-based triggers this service emits."),
  // ─── Service fee (subscription / recurring flat) ──────────────────────
  enableServiceFee: z92.boolean().optional().describe(
    "Emit a recurring service-fee trigger? When false, no service-fee trigger fires regardless of serviceFee value."
  ),
  serviceFee: currencyAmount().optional().describe("Service fee amount in dollars. Required when enableServiceFee=true."),
  // ─── Per-use fee (GuestRides only) ────────────────────────────────────
  // Moves does not support a per-use fee. The lite Moves bespokes
  // ship a $0.75 rideshare platform fee instead, baked into the
  // bespoke template (no contract.config slot for it).
  perUseFee: currencyAmount().optional().describe("Per-use fee in dollars (GuestRides platform fee)."),
  // ─── Mileage bands (priced via raterulegroup) ─────────────────────────
  enableMileageBands: z92.boolean().optional().describe(
    "Use mileage-band pricing for per-move charges? Bands emit per-move event triggers with pricing read from the contract's raterulegroup at runtime."
  ),
  // ─── Rideshare platform fee (lite Moves bespokes only) ────────────────
  // Locked on the bespoke template (not exposed as a generic Moves csConfig
  // field). Hoprides reads this when creating the rideshare accessorial on
  // the move, replacing the legacy customer-level fee read. AR runs on
  // the accessorial via existing accessorial predicates — no extra
  // trigger emission needed from the moves template.
  ridesharePlatformFee: currencyAmount().optional().describe(
    "Per-ride rideshare platform fee (dollars). Set by lite Moves bespokes; read by hoprides via getMoveService(contractId) when creating the rideshare accessorial."
  )
}).strict();
var schema93 = z92.object({
  services: z92.record(z92.string(), serviceEntrySchema).describe(
    'Per-service configuration keyed on the SDK template key (e.g. "valet"). One entry per applied template.'
  )
}).strict().describe(
  "AR service configuration namespace for a contract. Pricing values and toggle states for every service applied to this contract."
);
var definition99 = define({
  key: "ar",
  schema: schema93,
  label: "AR Services",
  description: "Per-contract Accounts Receivable service configuration. Stores pricing values and on/off toggles for every service (template) applied to a contract. Written by the SDK applyTemplate function; read by AR algorithms at run time.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.BILLING.key,
  // Storage level: LEVELS.CONTRACT (contracts table, formerly raterulegroup).
  // See file header convention #4 for why this is NOT LEVELS.WORKFLOWSET.
  writableAtLevels: [LEVELS.CONTRACT],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: {
    services: {}
  }
});

// src/definitions/mobile-service.ts
var mobile_service_exports = {};
__export(mobile_service_exports, {
  definition: () => definition100,
  schema: () => schema94
});
import { z as z93 } from "zod";
var schema94 = z93.object({
  active: z93.boolean().describe(
    "Activate/Deactivate the mobile service feature for this client. When inactive, the Mobile Service nav item and route are hidden in the dealer portal."
  ).optional()
}).strict().describe(
  "Mobile Service allows dealerships to submit completed mobile service runs for OEM reimbursement. Technicians record vehicle stops with VIN, mileage, and R/O data that are compiled into claim submissions."
);
var definition100 = define({
  key: "mobile_service",
  schema: schema94,
  label: "Mobile Service",
  description: "Mobile Service allows dealerships to submit completed mobile service runs for OEM reimbursement. Technicians record vehicle stops with VIN, mileage, and R/O data that are compiled into claim submissions.",
  warning: "Requires active mobility contract. Verify client contract before activating.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.MOBILITY.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  // Card #1184 made this dealer-visible; card #1411 took it back. `active` is the same
  // enablement lever as `guestrides.enabled` under a different name, and the owner ruled on
  // 2026-09-01 that enabling a product is not customer-facing configuration. The definition
  // holds only this one field, so there is nothing left for a dealer to see and the Mobile
  // Service page renders no Settings tab at all, exactly like Valet.
  readableByRoles: [ROLES.ADMIN],
  writableByRoles: [ROLES.ADMIN],
  defaultValue: {
    active: false
  }
});

// src/definitions/mobile-service-claims.ts
var mobile_service_claims_exports = {};
__export(mobile_service_claims_exports, {
  definition: () => definition101,
  schema: () => schema95
});
import { z as z94 } from "zod";
var schema95 = z94.object({
  active: z94.boolean().describe(
    "Activate/Deactivate the mobile service claims feature for this client. When inactive, the Mobile Service Claims nav item and routes are hidden in the dealer portal."
  ).optional()
}).strict().describe(
  "Mobile Service Claims allows dealerships to submit completed mobile service runs for OEM reimbursement. Technicians record vehicle stops with VIN, mileage, and R/O data that are compiled into claim submissions."
);
var definition101 = define({
  key: "mobile_service_claims",
  schema: schema95,
  label: "Mobile Service Claims",
  description: "Mobile Service Claims allows dealerships to submit completed mobile service runs for OEM reimbursement. Technicians record vehicle stops with VIN, mileage, and R/O data that are compiled into claim submissions.",
  warning: "Requires active mobility contract. Verify client contract before activating.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.MOBILITY.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  // Card #1184 made this dealer-visible; card #1411 took it back. `active` is the same
  // enablement lever as `guestrides.enabled` under a different name, and the owner ruled on
  // 2026-09-01 that enabling a product is not customer-facing configuration. This definition
  // holds only that one field, so the Mobile Service page has no dealer-visible settings left
  // and renders no Settings tab, exactly like Valet.
  readableByRoles: [ROLES.ADMIN],
  writableByRoles: [ROLES.ADMIN],
  defaultValue: {
    active: false
  }
});

// src/definitions/shuttle.ts
var shuttle_exports = {};
__export(shuttle_exports, {
  definition: () => definition102,
  schema: () => schema96
});
import { z as z95 } from "zod";
var schema96 = z95.object({
  active: z95.boolean().describe(
    "Activate/Deactivate the shuttle feature for this client. When inactive, the Shuttle nav item and route are hidden in the dealer portal."
  ).optional()
}).strict().describe(
  "Shuttle manages scheduled shuttle runs between dealership locations, allowing dealers to plan multi-stop routes for vehicle transport."
);
var definition102 = define({
  key: "shuttle",
  schema: schema96,
  label: "Shuttle",
  description: "Shuttle manages scheduled shuttle runs between dealership locations, allowing dealers to plan multi-stop routes for vehicle transport.",
  warning: "Requires active mobility contract. Verify client contract before activating.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.MOBILITY.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: {
    active: false
  }
});

// src/definitions/parts-delivery.ts
var parts_delivery_exports = {};
__export(parts_delivery_exports, {
  definition: () => definition103,
  schema: () => schema97
});
import { z as z96 } from "zod";
var schema97 = z96.object({
  active: z96.boolean().describe(
    "Activate/Deactivate the parts delivery feature for this client. When inactive, the Parts Delivery nav item and route are hidden in the dealer portal."
  ).optional(),
  third_party: z96.boolean().describe(
    "Enable HopDrive Managed couriers (3rd party): the dealer portal can quote and book Uber Direct / DoorDash Drive deliveries. Requires active."
  ).optional()
}).strict().describe(
  "Parts Delivery manages delivery runs for transporting parts between dealership locations and service centers."
);
var definition103 = define({
  key: "parts_delivery",
  schema: schema97,
  label: "Parts Delivery",
  description: "Parts Delivery manages delivery runs for transporting parts between dealership locations and service centers.",
  warning: "Requires active mobility contract. Verify client contract before activating.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.MOBILITY.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  // Card #1184 made this dealer-visible; card #1411 took it back, and #1323's open question
  // is now answered. `active` is the same enablement lever as `guestrides.enabled` under a
  // different name. `third_party` opens Uber Direct / DoorDash Drive booking, a spend channel
  // the dealer pays for, and it rode along with `active` because the shallow per-level merge
  // makes the key the write unit. Both are admin-only now, so the Parts Delivery page has no
  // dealer-visible settings left and renders no Settings tab, exactly like Valet.
  readableByRoles: [ROLES.ADMIN],
  writableByRoles: [ROLES.ADMIN],
  defaultValue: {
    active: false,
    third_party: false
  }
});

// src/definitions/driver-pipeline.ts
var driver_pipeline_exports = {};
__export(driver_pipeline_exports, {
  definition: () => definition104,
  schema: () => schema98
});
import { z as z97 } from "zod";
var schema98 = z97.object({
  funnel_open: z97.boolean().describe(
    "Whether the driver recruitment funnel is open for this region. When true, new drivers can be recruited."
  ).optional(),
  active_driver_target: z97.number().int().nonnegative().describe("Target number of active drivers for this region. Used to determine recruitment goals.").optional(),
  waitlist_target: z97.number().int().nonnegative().describe("Target number of drivers on the waitlist for this region. Maintains a pipeline of ready drivers.").optional(),
  backlog_target: z97.number().int().nonnegative().describe("Maximum number of drivers allowed in the backlog. When reached, recruitment funnel may be closed.").optional(),
  engagement_frequency_days: z97.number().int().positive().describe(
    "How often (in days) to engage with drivers in the pipeline. Used for scheduling follow-up communications."
  ).optional(),
  max_missed_confirmations: z97.number().int().nonnegative().describe(
    "Maximum number of missed confirmations allowed before removing a driver from the recruitment pipeline."
  ).optional()
}).strict().describe("Driver recruitment pipeline configuration for managing funnel state, targets, and engagement rules.");
var definition104 = define({
  key: "driver_pipeline",
  kind: DEFINITION_KINDS.SETTING,
  schema: schema98,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.REGION],
  readableByRoles: [ROLES.ADMIN],
  label: "Driver Pipeline",
  description: "Driver recruitment pipeline configuration with funnel management and engagement settings.",
  category: CATEGORIES.REGION_PIPELINE.key,
  storage: {
    // completely unneccessary since jsonb config column storage is the default
    levels: {
      region: {
        type: "jsonb",
        path: "driver_pipeline"
      }
    }
  },
  defaultValue: {
    funnel_open: false,
    active_driver_target: 20,
    waitlist_target: 25,
    backlog_target: 50,
    engagement_frequency_days: 7,
    max_missed_confirmations: 3
  }
});

// src/definitions/isShuttle.ts
var isShuttle_exports = {};
__export(isShuttle_exports, {
  definition: () => definition105,
  schema: () => schema99
});
import { z as z98 } from "zod";
var schema99 = z98.boolean().optional();
var definition105 = define({
  key: "isShuttle",
  schema: schema99,
  label: "Is Shuttle",
  description: "System flag marking this move as part of a shuttle sequence. Set by shuttle planning, not user-configurable.",
  kind: DEFINITION_KINDS.OUTPUT,
  category: CATEGORIES.OPERATIONAL_STATE.key,
  writableAtLevels: [LEVELS.MOVE],
  readableByRoles: [ROLES.ADMIN]
});

// src/definitions/firstShuttleMove.ts
var firstShuttleMove_exports = {};
__export(firstShuttleMove_exports, {
  definition: () => definition106,
  schema: () => schema100
});
import { z as z99 } from "zod";
var schema100 = z99.boolean().optional();
var definition106 = define({
  key: "firstShuttleMove",
  schema: schema100,
  label: "First Shuttle Move",
  description: "System flag marking this as the first move in a shuttle sequence. Set by shuttle planning, not user-configurable.",
  kind: DEFINITION_KINDS.OUTPUT,
  category: CATEGORIES.OPERATIONAL_STATE.key,
  writableAtLevels: [LEVELS.MOVE],
  readableByRoles: [ROLES.ADMIN]
});

// src/definitions/lastShuttleMove.ts
var lastShuttleMove_exports = {};
__export(lastShuttleMove_exports, {
  definition: () => definition107,
  schema: () => schema101
});
import { z as z100 } from "zod";
var schema101 = z100.boolean().optional();
var definition107 = define({
  key: "lastShuttleMove",
  schema: schema101,
  label: "Last Shuttle Move",
  description: 'System flag marking this as the last move in a shuttle sequence. Triggers chase vehicle status to "returned". Set by shuttle planning, not user-configurable.',
  kind: DEFINITION_KINDS.OUTPUT,
  category: CATEGORIES.OPERATIONAL_STATE.key,
  writableAtLevels: [LEVELS.MOVE],
  readableByRoles: [ROLES.ADMIN]
});

// src/definitions/chase-id.ts
var chase_id_exports = {};
__export(chase_id_exports, {
  definition: () => definition108,
  schema: () => schema102
});
import { z as z101 } from "zod";
var schema102 = z101.string().uuid().optional();
var definition108 = define({
  key: "chase_id",
  schema: schema102,
  label: "Chase ID",
  description: "UUID linking chase moves together for ridegroup management, cost allocation, and status sync. Set by shuttle/chase planning, not user-configurable.",
  kind: DEFINITION_KINDS.OUTPUT,
  category: CATEGORIES.OPERATIONAL_STATE.key,
  writableAtLevels: [LEVELS.MOVE],
  readableByRoles: [ROLES.ADMIN]
});

// src/definitions/parent-chase-id.ts
var parent_chase_id_exports = {};
__export(parent_chase_id_exports, {
  definition: () => definition109,
  schema: () => schema103
});
import { z as z102 } from "zod";
var schema103 = z102.string().uuid().optional();
var definition109 = define({
  key: "parent_chase_id",
  schema: schema103,
  label: "Parent Chase ID",
  description: "UUID referencing the parent move in a chase sequence. Set by autoplanner, not user-configurable.",
  kind: DEFINITION_KINDS.OUTPUT,
  category: CATEGORIES.OPERATIONAL_STATE.key,
  writableAtLevels: [LEVELS.MOVE],
  readableByRoles: [ROLES.ADMIN]
});

// src/definitions/batch.ts
var batch_exports = {};
__export(batch_exports, {
  definition: () => definition110,
  schema: () => schema104
});
import { z as z103 } from "zod";
var schema104 = z103.object({
  order_ref: z103.string().describe("External order reference from Super Dispatch").optional(),
  size: z103.number().int().positive().describe("Number of vehicles in this batch").optional()
}).strict().optional();
var definition110 = define({
  key: "batch",
  schema: schema104,
  label: "Batch",
  description: "Groups moves from multi-vehicle Super Dispatch orders. Contains order reference and batch size. Set by integrations, not user-configurable.",
  kind: DEFINITION_KINDS.OUTPUT,
  category: CATEGORIES.OPERATIONAL_STATE.key,
  writableAtLevels: [LEVELS.MOVE],
  readableByRoles: [ROLES.ADMIN]
});

// src/definitions/ride-orchestration.ts
var ride_orchestration_exports = {};
__export(ride_orchestration_exports, {
  RideshareProviderSchema: () => RideshareProviderSchema,
  definition: () => definition111,
  schema: () => schema105
});
import { z as z104 } from "zod";
var RideshareProviderSchema = z104.enum(["uber", "lyft"]);
var schema105 = z104.object({
  last_estimate_at: z104.string().describe("ISO timestamp of last estimate fetch").optional(),
  next_estimate_at: z104.string().describe("ISO timestamp for next scheduled estimate refresh").optional(),
  rideshare_eta_seconds: z104.number().describe("Estimated time of arrival in seconds").optional(),
  selected_provider: RideshareProviderSchema.describe("Selected rideshare provider (uber or lyft)").optional(),
  selected_product: z104.string().describe("Selected product/tier (e.g., UberX, Lyft XL)").optional(),
  target_pickup_time: z104.string().describe("Target pickup time for ride-after scenarios (ISO timestamp)").optional(),
  best_time_to_call: z104.string().describe("Optimal time to request the ride (ISO timestamp)").optional(),
  booking_lead_time_seconds: z104.number().describe("Lead time needed before target pickup").optional(),
  calculation_method: z104.string().describe('Method used to calculate timing (e.g., "eta-based", "fixed")').optional()
}).strict().optional();
var definition111 = define({
  key: "ride_orchestration",
  schema: schema105,
  label: "Ride Orchestration",
  description: "Rideshare booking state including estimates, provider selection, and timing calculations. Set by hoprides service, not user-configurable.",
  kind: DEFINITION_KINDS.OUTPUT,
  category: CATEGORIES.OPERATIONAL_STATE.key,
  writableAtLevels: [LEVELS.MOVE],
  readableByRoles: [ROLES.ADMIN]
});

// src/definitions/superDispatch.ts
var superDispatch_exports = {};
__export(superDispatch_exports, {
  definition: () => definition112,
  schema: () => schema106
});
import { z as z105 } from "zod";
var schema106 = z105.object({
  vehicle_id: z105.number().describe("Super Dispatch vehicle ID").optional(),
  reference_num: z105.number().describe("Super Dispatch reference number for status sync").optional()
}).strict().optional();
var definition112 = define({
  key: "superDispatch",
  schema: schema106,
  label: "Super Dispatch",
  description: "Super Dispatch integration reference data for status sync. Set by super-dispatch-integration, not user-configurable.",
  kind: DEFINITION_KINDS.OUTPUT,
  category: CATEGORIES.OPERATIONAL_STATE.key,
  writableAtLevels: [LEVELS.MOVE],
  readableByRoles: [ROLES.ADMIN]
});

// src/definitions/superdispatch-routing.ts
var superdispatch_routing_exports = {};
__export(superdispatch_routing_exports, {
  definition: () => definition113,
  schema: () => schema107
});
import { z as z106 } from "zod";
var schema107 = z106.object({
  enabled: z106.boolean().describe(
    "Master switch for this organization. When off, HopDrive stops ACCEPTING their Super Dispatch offers, so no new moves are created.\n\n**Moves that already exist keep syncing.** Their status changes, charges and photos still reach Super Dispatch."
  ).optional(),
  prefix: z106.string().describe(
    "Super Dispatch order numbers starting with this prefix belong to this organization \u2014 `TS` for Merchants Fleet, `HC` for Hertz. Case-insensitive, and the LONGEST matching prefix wins, so `HCL` beats `HC` when both exist. An order matching no organization falls back to a default set in the integration code."
  ).optional(),
  autoAcceptOffer: z106.boolean().describe(
    "Accept Super Dispatch load offers for this organization automatically, instead of leaving them for a human. Requires an explicit prefix match \u2014 an order that only reached this organization through the code-level fallback is never auto-accepted."
  ).optional(),
  ghostMoveOffer: z106.boolean().describe(
    'Accept offers whose vehicle details are still placeholders (make and model literally "make" and "model"). Some shippers send the offer before the real vehicle is known.\n\n**A placeholder offer left unaccepted is not revisited.** HopDrive does not hold it and accept it once the real vehicle arrives \u2014 it waits for a human, and is only re-evaluated if Super Dispatch sends the offer again.'
  ).optional()
}).strict().describe("How incoming Super Dispatch offers are routed to this organization.");
var definition113 = define({
  key: "superdispatch_routing",
  schema: schema107,
  label: "Super Dispatch",
  description: "Whether this organization's Super Dispatch orders are accepted, which order numbers belong to them, and how their offers are handled. Set this to bring a new Super Dispatch client online. What HopDrive sends BACK to Super Dispatch is configured separately, under Super Dispatch Sync.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.SUPER_DISPATCH.key,
  warning: "Two organizations must never claim the same prefix \u2014 orders would route to whichever matched first. The longest matching prefix wins, so a more specific prefix can safely sit alongside a shorter one.",
  // ORGANIZATION IS THE HOME; A CUSTOMER MAY OVERRIDE. The useful direction is
  // opt-IN: switching an organization off and two of its three hundred rooftops
  // back on is one edit plus two, where the inverse is two hundred and
  // ninety-eight. `enabled` is read from the merged stack once the customer is
  // known (integrations/super-dispatch acceptsNewWorkForCustomer), so a customer
  // row saying `true` beats the organization's `false`.
  //
  // `prefix` is a different matter and is NOT yet honoured per customer: it is
  // what resolves the ORGANIZATION, before any customer exists, and a rooftop is
  // then found by delivery address rather than by prefix. Setting it on a
  // customer currently changes nothing — see the follow-up on customer-level
  // prefix routing.
  writableAtLevels: [LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN]
});

// src/definitions/superdispatch-subscription.ts
var superdispatch_subscription_exports = {};
__export(superdispatch_subscription_exports, {
  definition: () => definition114,
  schema: () => schema108
});
import { z as z107 } from "zod";
var schema108 = z107.object({}).strict().describe("Managed directly against the subscriptions table; nothing is stored in config.");
var definition114 = define({
  key: "superdispatch_subscription",
  schema: schema108,
  label: "Super Dispatch Subscription",
  description: "Whether Super Dispatch receives webhooks about this customer's moves. Read live from the subscriptions table and changed here directly \u2014 this is not stored in config, so there is only ever one answer.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.SUPER_DISPATCH.key,
  writableAtLevels: [LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN]
});

// src/definitions/carsarrive-routing.ts
var carsarrive_routing_exports = {};
__export(carsarrive_routing_exports, {
  definition: () => definition115,
  schema: () => schema109
});
import { z as z108 } from "zod";
var schema109 = z108.object({
  enabled: z108.boolean().describe(
    "Master switch for this organization. When off, HopDrive stops ACCEPTING their CarsArrive loads \u2014 an assigned load is rejected back to CarsArrive rather than becoming a move.\n\n**Moves that already exist keep reporting.** Their status changes, photos and invoice still reach CarsArrive.\n\nThis does not stop CarsArrive quoting: a `Quote:DriveService` request is priced as usual, because a quote creates nothing and refusing to price one looks to CarsArrive like an outage rather than a decision."
  ).optional()
}).strict().describe("Whether incoming CarsArrive loads are accepted for this organization.");
var definition115 = define({
  key: "carsarrive_routing",
  schema: schema109,
  label: "CarsArrive",
  description: "Whether this organization's CarsArrive loads are accepted. What HopDrive sends BACK to CarsArrive is configured separately, under CarsArrive Sync; getting an individual rooftop live is under CarsArrive Connection.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.CARS_ARRIVE.key,
  writableAtLevels: [LEVELS.ORGANIZATION],
  readableByRoles: [ROLES.ADMIN]
});

// src/definitions/carsarrive-connection.ts
var carsarrive_connection_exports = {};
__export(carsarrive_connection_exports, {
  definition: () => definition116,
  schema: () => schema110
});
import { z as z109 } from "zod";
var schema110 = z109.object({}).strict().describe("Managed directly against the subscriptions table; nothing is stored in config.");
var definition116 = define({
  key: "carsarrive_connection",
  schema: schema110,
  label: "CarsArrive Connection",
  description: "What it takes to get this rooftop live with CarsArrive: the manual step CarsArrive has to do on their side, and the webhook subscription that lets HopDrive report the move back. Read live from the subscriptions table and changed here directly \u2014 this is not stored in config, so there is only ever one answer.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.CARS_ARRIVE.key,
  writableAtLevels: [LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN]
});

// src/definitions/referenceNumbers.ts
var referenceNumbers_exports = {};
__export(referenceNumbers_exports, {
  definition: () => definition117,
  schema: () => schema111
});
import { z as z110 } from "zod";
var schema111 = z110.record(z110.string()).optional();
var definition117 = define({
  key: "referenceNumbers",
  schema: schema111,
  label: "Reference Numbers",
  description: "External system reference IDs from CarsArrive, Central Dispatch, etc. Set by integrations, not user-configurable.",
  kind: DEFINITION_KINDS.OUTPUT,
  category: CATEGORIES.OPERATIONAL_STATE.key,
  writableAtLevels: [LEVELS.MOVE],
  readableByRoles: [ROLES.ADMIN]
});

// src/definitions/isGhost.ts
var isGhost_exports = {};
__export(isGhost_exports, {
  definition: () => definition118,
  schema: () => schema112
});
import { z as z111 } from "zod";
var schema112 = z111.boolean().optional();
var definition118 = define({
  key: "isGhost",
  schema: schema112,
  label: "Is Ghost",
  description: "Marks a placeholder move requiring full VIN capture. Set when created from Super Dispatch with shell vehicle data. Not user-configurable.",
  kind: DEFINITION_KINDS.OUTPUT,
  category: CATEGORIES.OPERATIONAL_STATE.key,
  writableAtLevels: [LEVELS.MOVE],
  readableByRoles: [ROLES.ADMIN]
});

// src/definitions/vin.ts
var vin_exports = {};
__export(vin_exports, {
  definition: () => definition119,
  schema: () => schema113
});
import { z as z112 } from "zod";
var schema113 = z112.object({
  capture: z112.enum(["full", "partial", "none"]).describe("Required VIN capture level in driver workflow").optional()
}).strict().optional();
var definition119 = define({
  key: "vin",
  schema: schema113,
  label: "VIN Capture",
  description: "VIN capture requirements for driver workflow. Set by system for ghost moves, not user-configurable.",
  kind: DEFINITION_KINDS.OUTPUT,
  category: CATEGORIES.OPERATIONAL_STATE.key,
  writableAtLevels: [LEVELS.MOVE],
  readableByRoles: [ROLES.ADMIN]
});

// src/definitions/triangle-move.ts
var triangle_move_exports = {};
__export(triangle_move_exports, {
  definition: () => definition120,
  schema: () => schema114
});
import { z as z113 } from "zod";
var schema114 = z113.boolean().optional();
var definition120 = define({
  key: "triangle_move",
  schema: schema114,
  label: "Triangle Move",
  description: "Marks move as triangle (ride before AND after charged to customer). Set via admin UI when customer has allow_triangle_moves permission.",
  kind: DEFINITION_KINDS.OUTPUT,
  category: CATEGORIES.OPERATIONAL_STATE.key,
  writableAtLevels: [LEVELS.MOVE],
  readableByRoles: [ROLES.ADMIN]
});

// src/definitions/isFlex.ts
var isFlex_exports = {};
__export(isFlex_exports, {
  definition: () => definition121,
  schema: () => schema115
});
import { z as z114 } from "zod";
var schema115 = z114.boolean().optional();
var definition121 = define({
  key: "isFlex",
  schema: schema115,
  label: "Is Flex",
  description: "Marks a round trip as flex type for scenario detection. Set by system, not user-configurable.",
  kind: DEFINITION_KINDS.OUTPUT,
  category: CATEGORIES.OPERATIONAL_STATE.key,
  writableAtLevels: [LEVELS.MOVE],
  readableByRoles: [ROLES.ADMIN]
});

// src/definitions/moveInput.ts
var moveInput_exports = {};
__export(moveInput_exports, {
  definition: () => definition122,
  schema: () => schema116
});
import { z as z115 } from "zod";
var schema116 = z115.record(z115.string(), z115.unknown()).describe(
  "Output data from the dynamic form presented during move create in React. Contains indeterminate key-value pairs (one level deep). Not user-configurable."
);
var definition122 = define({
  key: "moveInput",
  schema: schema116,
  label: "Move Input",
  description: "Output data from the dynamic form presented during move create in React. Contains indeterminate key-value pairs (one level deep). Not user-configurable.",
  kind: DEFINITION_KINDS.OUTPUT,
  category: CATEGORIES.OPERATIONAL_STATE.key,
  writableAtLevels: [LEVELS.MOVE],
  readableByRoles: [ROLES.ADMIN]
});

// src/definitions/loaner-vin-edit-disabled.ts
var loaner_vin_edit_disabled_exports = {};
__export(loaner_vin_edit_disabled_exports, {
  definition: () => definition123,
  schema: () => schema117
});
import { z as z116 } from "zod";
var schema117 = z116.boolean().optional();
var definition123 = define({
  key: "loaner_vin_edit_disabled",
  schema: schema117,
  label: "Disable Loaner VIN Editing",
  description: "When enabled, prevents editing the loaner vehicle VIN field in move details.",
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.MOVE_PLANNING.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: false
});

// src/definitions/guestrides-split.ts
var GUESTRIDES_KEY = "guestrides";
var GUESTRIDES_ACCESS_KEY = "guestrides_access";
var GUESTRIDES_ACCESS_FIELDS = [
  "enabled",
  "credit_limit",
  "credit_warning_threshold",
  "marketable",
  "algorithm_key"
];
var GUESTRIDES_DEALER_FIELDS = [
  "guest_portal_enabled",
  "max_trip_miles",
  "require_approval",
  "approver_user_id",
  "approval_notification_channels"
];
var asSection = (value) => typeof value === "object" && value !== null && !Array.isArray(value) ? value : void 0;
function readGuestridesAccessSection(config) {
  if (!config) return void 0;
  const legacy = asSection(config[GUESTRIDES_KEY]);
  const current = asSection(config[GUESTRIDES_ACCESS_KEY]);
  if (!legacy && !current) return void 0;
  const merged = {};
  if (legacy) {
    for (const field of GUESTRIDES_ACCESS_FIELDS) {
      if (field in legacy) merged[field] = legacy[field];
    }
  }
  if (current) Object.assign(merged, current);
  return merged;
}
function splitGuestridesConfig(config) {
  const legacy = asSection(config[GUESTRIDES_KEY]);
  if (!legacy) return config;
  const moved = {};
  const kept = {};
  for (const [field, value] of Object.entries(legacy)) {
    if (GUESTRIDES_ACCESS_FIELDS.includes(field)) moved[field] = value;
    else kept[field] = value;
  }
  if (Object.keys(moved).length === 0) return config;
  const next = { ...config };
  next[GUESTRIDES_ACCESS_KEY] = { ...moved, ...asSection(config[GUESTRIDES_ACCESS_KEY]) ?? {} };
  if (Object.keys(kept).length > 0) next[GUESTRIDES_KEY] = kept;
  else delete next[GUESTRIDES_KEY];
  return next;
}

// src/definitions/index.ts
var allDefinitions = {
  [definition7.key]: definition7,
  [definition8.key]: definition8,
  [definition9.key]: definition9,
  [definition10.key]: definition10,
  [definition11.key]: definition11,
  [definition12.key]: definition12,
  [definition13.key]: definition13,
  [definition14.key]: definition14,
  [definition15.key]: definition15,
  [definition16.key]: definition16,
  [definition17.key]: definition17,
  [definition18.key]: definition18,
  [definition19.key]: definition19,
  [definition20.key]: definition20,
  [definition21.key]: definition21,
  [definition22.key]: definition22,
  [definition23.key]: definition23,
  [definition24.key]: definition24,
  [definition25.key]: definition25,
  [definition26.key]: definition26,
  [definition27.key]: definition27,
  [definition28.key]: definition28,
  [definition29.key]: definition29,
  [definition30.key]: definition30,
  [definition31.key]: definition31,
  [definition.key]: definition,
  [definition32.key]: definition32,
  [definition33.key]: definition33,
  [definition34.key]: definition34,
  [definition35.key]: definition35,
  [definition36.key]: definition36,
  [definition37.key]: definition37,
  [definition38.key]: definition38,
  [definition39.key]: definition39,
  [definition40.key]: definition40,
  [definition41.key]: definition41,
  [definition42.key]: definition42,
  [definition43.key]: definition43,
  [definition44.key]: definition44,
  [definition45.key]: definition45,
  [definition46.key]: definition46,
  [definition47.key]: definition47,
  [definition48.key]: definition48,
  [definition49.key]: definition49,
  [definition50.key]: definition50,
  [definition51.key]: definition51,
  [definition52.key]: definition52,
  [definition53.key]: definition53,
  [definition54.key]: definition54,
  [definition55.key]: definition55,
  [definition2.key]: definition2,
  [definition56.key]: definition56,
  [definition57.key]: definition57,
  [definition58.key]: definition58,
  [definition59.key]: definition59,
  [definition60.key]: definition60,
  [definition61.key]: definition61,
  [definition62.key]: definition62,
  [definition63.key]: definition63,
  [definition64.key]: definition64,
  [definition65.key]: definition65,
  [definition66.key]: definition66,
  [definition67.key]: definition67,
  [definition68.key]: definition68,
  [definition69.key]: definition69,
  [definition70.key]: definition70,
  [definition71.key]: definition71,
  [definition72.key]: definition72,
  [definition73.key]: definition73,
  [definition74.key]: definition74,
  [definition75.key]: definition75,
  [definition76.key]: definition76,
  [definition77.key]: definition77,
  [definition78.key]: definition78,
  [definition79.key]: definition79,
  [definition80.key]: definition80,
  [definition81.key]: definition81,
  [definition82.key]: definition82,
  [definition83.key]: definition83,
  [definition84.key]: definition84,
  [definition3.key]: definition3,
  [definition85.key]: definition85,
  [definition86.key]: definition86,
  [definition87.key]: definition87,
  [definition4.key]: definition4,
  [definition88.key]: definition88,
  [definition89.key]: definition89,
  [definition90.key]: definition90,
  [definition91.key]: definition91,
  [definition97.key]: definition97,
  [definition98.key]: definition98,
  [definition99.key]: definition99,
  // Mobility product definitions
  [definition100.key]: definition100,
  [definition101.key]: definition101,
  [definition102.key]: definition102,
  [definition103.key]: definition103,
  // Driver pipeline definitions
  [definition104.key]: definition104,
  [definition92.key]: definition92,
  // Non-JSONB storage definitions
  [definition93.key]: definition93,
  [definition94.key]: definition94,
  [definition95.key]: definition95,
  [definition96.key]: definition96,
  // Operational state definitions
  [definition105.key]: definition105,
  [definition106.key]: definition106,
  [definition107.key]: definition107,
  [definition108.key]: definition108,
  [definition109.key]: definition109,
  [definition110.key]: definition110,
  [definition111.key]: definition111,
  [definition112.key]: definition112,
  // ORDER MATTERS: the settings UI lists a category's definitions in the order
  // they appear here. Routing first (how orders arrive), then Sync (what we
  // send back), then Subscription (whether this rooftop hears about it) — the
  // sequence the work actually happens in.
  [definition113.key]: definition113,
  [definition5.key]: definition5,
  [definition114.key]: definition114,
  // ORDER MATTERS here too, and for a sharper reason than Super Dispatch's.
  // Connection leads because it carries the one step HopDrive cannot perform or
  // verify — CarsArrive adding the customer to the API key — and skipping that
  // stops loads becoming moves at all. Everything below it is a refinement of
  // an integration that is already working. See carsarrive-connection.ts.
  [definition116.key]: definition116,
  [definition115.key]: definition115,
  [definition6.key]: definition6,
  [definition117.key]: definition117,
  [definition118.key]: definition118,
  [definition119.key]: definition119,
  [definition120.key]: definition120,
  [definition121.key]: definition121,
  [definition122.key]: definition122,
  [definition123.key]: definition123
};

export {
  version_exports,
  api_enabled_exports,
  ENCRYPTED_STRING_PATTERN,
  encryptedString,
  isEncryptedString,
  getEncryptionVersion,
  getCiphertext,
  api_config_exports,
  price_estimates_exports,
  distance_estimates_exports,
  allow_email_notifications_exports,
  notify_contact_of_outcomes_exports,
  dealer_visibility_enabled_exports,
  customer_feedback_recipients_fallback_exports,
  allow_triangle_moves_exports,
  enable_csv_import_exports,
  public_workflowsets_exports,
  rideshare_calling_enabled_exports,
  ride_provider_algorithm_key_exports,
  passthrough_rideshare_surges_exports,
  require_third_party_payer_exports,
  auto_assign_ref_exports,
  MoveTypeSchema,
  default_move_type_exports,
  allowed_move_types_exports,
  allowed_templates_exports,
  add_moves_always_exports,
  sixt_settings_exports,
  add_moves_disabled_exports,
  cancellation_fee_exports,
  delay_price_per_minute_exports,
  fuel_multiplier_exports,
  default_workflow_set_id_exports,
  pickup_workflow_id_exports,
  delivery_workflow_id_exports,
  default_pickup_workflow_id_exports,
  default_delivery_workflow_id_exports,
  move_workflow_overrides_exports,
  default_payer_exports,
  reference_num_label_exports,
  reference_num_required_for_pickup_exports,
  reference_num_required_for_return_exports,
  reference_num_report_enabled_exports,
  reference_num_report_label_exports,
  reporting_exports,
  serviceable_radius_miles_exports,
  customer_serviceable_radius_miles_exports,
  serviceable_radius_by_delivery_exports,
  serviceable_radius_by_pickup_exports,
  RideTypeOverrideSchema,
  ride_type_override_exports,
  branding_exports,
  AppraisalTimestampSchema,
  appraisals_exports,
  appointments_exports,
  appointment_management_exports,
  hopdrive_contacts_exports,
  fuel_exports,
  fraud_audit_exports,
  gps_transition_mode_exports,
  arrival_policy_arrive_exports,
  arrival_policy_depart_exports,
  pickup_start_gate_exports,
  gps_optimistic_arrivals_exports,
  gps_optimistic_departures_exports,
  geofence_default_radius_meters_exports,
  geofence_corroboration_window_minutes_exports,
  geofence_corroboration_min_pings_exports,
  geofence_corroboration_accuracy_cutoff_meters_exports,
  geofence_corroboration_drift_buffer_meters_exports,
  gps_stale_pickup_started_minutes_exports,
  gps_stale_pickup_arrived_minutes_exports,
  gps_stale_pickup_successful_minutes_exports,
  gps_stale_delivery_started_minutes_exports,
  gps_stale_delivery_arrived_minutes_exports,
  route_corridor_off_route_exports,
  route_deviation_alerts_exports,
  route_corridor_rejoin_exports,
  route_gps_accuracy_cutoff_exports,
  route_stop_dwell_threshold_exports,
  route_stop_radius_exports,
  route_stop_on_route_distance_exports,
  route_compliance_excess_distance_exports,
  route_compliance_excess_time_exports,
  program_visuals_exports,
  program_reporting_exports,
  guestrides_access_exports,
  product_history_exports,
  dms_exports,
  EmailScrapePipelineStepSchema,
  EmailScrapeMappingSchema,
  email_scrape_config_exports,
  RcEmailScrapeConfigSchema,
  rc_config_exports,
  private_exports,
  routing_exports,
  AUTOCOMPLETE_FIELDS,
  FETCH_RESULT_FIELDS,
  TransformConfigSchema,
  FieldMappingSchema,
  AuthProviderSchema,
  AutocompleteTriggerSchema,
  FetchTriggerSchema,
  FetchResultMappingsSchema,
  EnrichmentSchema,
  IntegrationSchema,
  connector_triggers_exports,
  minimum_certification_ids_exports,
  billing_frequency_exports,
  payment_terms_exports,
  auto_pay_exports,
  allow_vinless_valet_exports,
  auto_create_valet_moves_exports,
  ar_exports,
  mobile_service_exports,
  mobile_service_claims_exports,
  shuttle_exports,
  parts_delivery_exports,
  driver_pipeline_exports,
  isShuttle_exports,
  firstShuttleMove_exports,
  lastShuttleMove_exports,
  chase_id_exports,
  parent_chase_id_exports,
  batch_exports,
  RideshareProviderSchema,
  ride_orchestration_exports,
  superDispatch_exports,
  superdispatch_routing_exports,
  superdispatch_subscription_exports,
  carsarrive_routing_exports,
  carsarrive_connection_exports,
  referenceNumbers_exports,
  isGhost_exports,
  vin_exports,
  triangle_move_exports,
  isFlex_exports,
  moveInput_exports,
  loaner_vin_edit_disabled_exports,
  GUESTRIDES_KEY,
  GUESTRIDES_ACCESS_KEY,
  GUESTRIDES_ACCESS_FIELDS,
  GUESTRIDES_DEALER_FIELDS,
  readGuestridesAccessSection,
  splitGuestridesConfig,
  allDefinitions
};
//# sourceMappingURL=chunk-BAQPEJWK.mjs.map